<template>
  <transition name="v2-support-modal">
    <div
      v-if="open"
      class="v2-support-modal"
      @click.self="onBackdropClick"
    >
      <div
        ref="panelRef"
        class="v2-support-modal__panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="stepHeadingId"
      >
        <!-- Floating close button — sits over the hero column (desktop) or
             top-right of the form (mobile). The previous white topbar with
             a "1 / 3" chip ate ~52px of vertical real estate; this pattern
             matches the donation widget's free-floating close button. -->
        <button
          ref="closeBtnRef"
          type="button"
          class="v2-support-modal__close"
          aria-label="Close"
          @click="close"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>

        <!-- Inline-auth success toast — appears briefly when login or
             registration completes, while the wizard auto-advances. -->
        <transition name="v2-support-modal-toast">
          <div
            v-if="toastMessage"
            class="v2-support-modal__toast"
            role="status"
            aria-live="polite"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            <span>{{ toastMessage }}</span>
          </div>
        </transition>

        <!-- Body split: left form, right hero -->
        <div class="v2-support-modal__split">
          <section class="v2-support-modal__form">
            <!-- Dot stepper — mirrors the donation widget's progress
                 indicator (gray dots → green active pill). role=presentation
                 because the singular h2 + sr-only "Step X of 3" live region
                 already carry the position announcement for AT. -->
            <div class="v2sm-stepper" role="presentation" aria-hidden="true">
              <span
                v-for="n in 3"
                :key="n"
                class="v2sm-stepper__dot"
                :class="{
                  'is-active': n === currentStep,
                  'is-passed': n < currentStep,
                }"
              ></span>
            </div>

            <!-- Heading crossfades on step change. mode="out-in" keeps the
                 aria-labelledby target singular (only one h2 exists at any
                 moment) so screen readers reference a stable element.
                 The outcome step (3) suppresses the heading entirely —
                 the success card has its own visual identity ("You're
                 Live · Your campaign is live!") and the modal h2 above it
                 was repeating "All set" in a smaller voice. -->
            <transition name="v2sm-heading" mode="out-in">
              <h2
                v-if="currentStep !== 3"
                :id="stepHeadingId"
                :key="currentStep"
                class="v2-support-modal__heading"
              >
                {{ currentHeading }}
              </h2>
            </transition>
            <!-- Separate live region for step-change announcements so the
                 heading (which is the dialog's aria-labelledby target) doesn't
                 re-announce on every currentStep transition. -->
            <div class="v2-support-modal__sr-only" aria-live="polite" aria-atomic="true">
              Step {{ currentStep }} of 3
            </div>

            <!-- Steps stage: each step lives in its own absolutely-positioned
                 pane so a forward-bound step can slide in from the right while
                 the leaving step slides out to the left at the same time
                 (mirrors the MakeDonationWidget pattern). The pane is the
                 actual scroll container — overflow-y: auto lives on the pane
                 itself so the slide animation transforms the entire scrolled
                 surface. -->
            <div class="v2-support-modal__steps-stage">
              <transition
                :name="stepTransitionName"
                @before-enter="onStepBeforeEnter"
                @after-enter="onStepAfterEnter"
                @after-leave="onStepAfterLeave"
              >
              <div v-if="currentStep === 1" class="v2-support-modal__step-pane v2-support-modal__step-pane--step1"><div class="v2-support-modal__step">
                <!-- Mobile-only filler image. The right-column hero is
                     display:none on mobile (frees vertical room on Steps 2–3),
                     which left Step 1's short email form floating with a big
                     empty gap under the heading. Rick 2026-06-06: "add a filler
                     image + space above the title." Pin a Dig Pink fundraise
                     photo directly under the heading so the create screen reads
                     as intentional rather than half-empty. -->
                <img
                  class="v2-support-modal__mobile-filler"
                  src="/img/dig-pink-v2/fundraise-create.jpg"
                  alt=""
                  aria-hidden="true"
                />
                <!-- Logged-in state -->
                <div v-if="authState === 'logged_in'" class="v2-support-modal__welcome">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="24" height="24" aria-hidden="true">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                  </svg>
                  <div>
                    <h3>Welcome back!</h3>
                    <p>You're signed in as <strong>{{ form.email }}</strong>. We'll publish your campaign as soon as you finish the details.</p>
                  </div>
                </div>

                <template v-else>
                  <!-- Intro + roadmap collapse the moment the user commits to
                       an email so the InlineAuth login/registration form has
                       vertical room. The wrapper uses the grid-template-rows
                       0fr/1fr trick so we animate from the actual content
                       height to zero (no fixed max-height cap to mis-tune).
                       Inner overflow:hidden + min-height:0 lets the grid row
                       collapse smoothly. The flag flips back when resetState
                       clears emailStatus, so the intro reappears on reopen. -->
                  <div
                    :class="['v2-support-modal__lead-wrap', { 'is-collapsed': showInlineAuthWidget }]"
                    :aria-hidden="showInlineAuthWidget ? 'true' : 'false'"
                  >
                    <div class="v2-support-modal__lead">
                      <p class="v2-support-modal__intro">
                        Build a personal fundraising page that rolls up to <strong>{{ parentCampaign.name }}</strong>. Every dollar your network gives lifts the parent campaign's total.
                      </p>

                      <!-- "What's next" preview — three mini steps so visitors know
                           the commitment is small (4 quick screens, not a long form). -->
                      <ol class="v2-support-modal__roadmap" aria-label="What you'll do next">
                        <li class="v2-support-modal__roadmap-item">
                          <span class="v2-support-modal__roadmap-num">1</span>
                          <span class="v2-support-modal__roadmap-text">
                            <strong>Confirm your email</strong>
                            <small>We'll sign you in or set up an account.</small>
                          </span>
                        </li>
                        <li class="v2-support-modal__roadmap-item">
                          <span class="v2-support-modal__roadmap-num">2</span>
                          <span class="v2-support-modal__roadmap-text">
                            <strong>Name your campaign</strong>
                            <small>Pick a goal and tell us about your team.</small>
                          </span>
                        </li>
                        <li class="v2-support-modal__roadmap-item">
                          <span class="v2-support-modal__roadmap-num">3</span>
                          <span class="v2-support-modal__roadmap-text">
                            <strong>Set your event date</strong>
                            <small>Then start sharing — your page goes live instantly.</small>
                          </span>
                        </li>
                      </ol>
                    </div>
                  </div>

                  <!-- Email input (unauth) -->
                  <div class="v2-support-modal__field">
                    <label for="v2sm-email">Email Address</label>
                    <div class="v2-support-modal__input-wrap">
                      <input
                        id="v2sm-email"
                        v-model.trim="form.email"
                        type="email"
                        autocomplete="email"
                        placeholder="your@email.com"
                        :aria-invalid="!!errors.email"
                        @input="onEmailInput"
                        @blur="checkEmailExists"
                      />
                      <span
                        v-if="emailStatus === 'checking'"
                        class="v2-support-modal__spinner"
                        aria-hidden="true"
                      ></span>
                    </div>
                    <p v-if="errors.email" class="v2-support-modal__err">{{ errors.email }}</p>
                    <p v-if="emailStatus === 'exists' && authState !== 'exists_signed_in'" class="v2-support-modal__hint v2-support-modal__hint--info">
                      Welcome back! Sign in below to continue.
                    </p>
                    <p v-if="emailStatus === 'exists' && authState === 'exists_signed_in'" class="v2-support-modal__hint v2-support-modal__hint--success">
                      Signed in — keep going.
                    </p>
                    <p v-if="emailStatus === 'new' && !showInlineAuthWidget" class="v2-support-modal__hint">
                      Looks like you're new here — create your account below to continue.
                    </p>

                    <!-- Inline auth widget renders for BOTH existing (login) and new (registration)
                         emails. userExists toggles which form InlineAuth displays. :show is bound to
                         a data field that flips false→true in nextTick so InlineAuth's watch.show
                         fires and reveals the form (the component only exposes its fields on show
                         CHANGE, not on initial mount). -->
                    <inline-auth
                      v-if="(emailStatus === 'exists' || emailStatus === 'new') && authState !== 'exists_signed_in'"
                      :show="showInlineAuthWidget"
                      :user-exists="emailStatus === 'exists'"
                      :user-email="form.email"
                      @auth-success="onInlineAuthSuccess"
                    />
                  </div>
                </template>
              </div></div>
              </transition>

              <transition
                :name="stepTransitionName"
                @before-enter="onStepBeforeEnter"
                @after-enter="onStepAfterEnter"
                @after-leave="onStepAfterLeave"
              >
              <div v-if="currentStep === 2" class="v2-support-modal__step-pane"><div class="v2-support-modal__step v2-support-modal__step--details">
                  <!-- Mobile-only hero band. The right-column hero is display:none
                       on mobile, so Step 2 ("Tell us about your campaign") otherwise
                       opened straight into the form with no imagery. Pin the same
                       vertical fundraise photo this step's desktop hero already uses,
                       cropped to a short cover band so the portrait shot reads as an
                       intentional header rather than a tall image that gets cut off. -->
                  <img
                    class="v2-support-modal__mobile-filler v2-support-modal__mobile-filler--step2"
                    src="/img/dig-pink-v2/fundraise-share.jpg"
                    alt=""
                    aria-hidden="true"
                  />
                  <!-- THE BASICS — name + organization, prominent, with personality copy -->
                  <section class="v2-support-modal__section">
                    <span class="v2-support-modal__eyebrow">The Basics</span>
                    <div class="v2-support-modal__field v2-support-modal__field--lead">
                      <label for="v2sm-name">What should we call your campaign?</label>
                      <input
                        id="v2sm-name"
                        v-model.trim="form.name"
                        type="text"
                        maxlength="250"
                        placeholder="The Bulldogs Dig Pink Rally"
                        :aria-invalid="!!errors.name"
                      />
                      <small v-if="!errors.name" class="v2-support-modal__field-hint">Make it yours — donors will recognize you by this name.</small>
                      <p v-if="errors.name" class="v2-support-modal__err">{{ errors.name }}</p>
                    </div>

                    <div class="v2-support-modal__field-row">
                      <div class="v2-support-modal__field v2-support-modal__field--grow">
                        <label for="v2sm-business">Organization</label>
                        <input
                          id="v2sm-business"
                          v-model.trim="form.business_name"
                          type="text"
                          maxlength="250"
                          placeholder="Westminster High School"
                          :aria-invalid="!!errors.business_name"
                        />
                        <p v-if="errors.business_name" class="v2-support-modal__err">{{ errors.business_name }}</p>
                      </div>
                      <div class="v2-support-modal__field v2-support-modal__field--type">
                        <label for="v2sm-type">Type</label>
                        <select id="v2sm-type" v-model="form.campaign_type">
                          <option value="">Choose…</option>
                          <option value="school">School</option>
                          <option value="club">Club</option>
                          <option value="business">Business</option>
                          <option value="tournament">Tournament</option>
                          <option value="individual">Individual</option>
                        </select>
                      </div>
                    </div>
                  </section>

                  <!-- YOUR GOAL — visceral chicklets, premium feel -->
                  <section class="v2-support-modal__section">
                    <span class="v2-support-modal__eyebrow">Your Goal</span>
                    <span class="v2-support-modal__section-prompt">How much do you want to raise?</span>
                    <div class="v2-support-modal__goalrow">
                      <div class="v2-support-modal__pills" role="radiogroup" aria-label="Fundraising goal">
                        <button
                          v-for="preset in goalPresets"
                          :key="preset"
                          type="button"
                          class="v2-support-modal__pill"
                          :class="{ 'is-active': form.goal === String(preset) }"
                          :aria-pressed="form.goal === String(preset)"
                          @click="selectGoalPreset(preset)"
                        >
                          <span class="v2-support-modal__pill-amount">${{ preset.toLocaleString() }}</span>
                        </button>
                        <!-- Custom amount lives in the same grid as the preset
                             pills — second row, full width — so it reads as a
                             sibling option rather than a separate "fancy
                             input" stack. Matches the pill's height, font,
                             border, and radius; the only difference is the
                             leading $ glyph and an editable number. -->
                        <label
                          class="v2-support-modal__custom-pill"
                          :class="{ 'is-active': form.goal === 'other' }"
                        >
                          <span class="v2-support-modal__custom-pill-glyph" aria-hidden="true">$</span>
                          <input
                            v-model.number="form.goal_other"
                            type="number"
                            min="100"
                            step="100"
                            placeholder="Custom amount"
                            aria-label="Custom fundraising goal"
                            :aria-invalid="!!errors.goal_other"
                            @input="onCustomGoalInput"
                          />
                        </label>
                      </div>
                      <p v-if="errors.goal_other" class="v2-support-modal__err">{{ errors.goal_other }}</p>
                    </div>
                  </section>

                  <!-- EVENT — city, state, and date describe the event in one tight row.
                       The agreement checkbox lives at the BOTTOM of this section
                       (separated by a hairline divider) so it reads as the natural
                       closing row of the form rather than a footer attached to the
                       buttons. Per Rick: "I agree…" should feel connected to the
                       data being provided. -->
                  <section class="v2-support-modal__section">
                    <span class="v2-support-modal__eyebrow">Event</span>
                    <div class="v2-support-modal__event-row">
                      <div class="v2-support-modal__field v2-support-modal__field--grow">
                        <label for="v2sm-city">City</label>
                        <input
                          id="v2sm-city"
                          v-model.trim="form.campaign_type_city"
                          type="text"
                          maxlength="250"
                          :placeholder="parentCampaign.city || 'Your town'"
                        />
                      </div>
                      <div class="v2-support-modal__field v2-support-modal__field--state">
                        <label for="v2sm-state">State</label>
                        <select
                          id="v2sm-state"
                          v-model="form.campaign_type_state"
                        >
                          <option value="">--</option>
                          <option
                            v-for="s in usStates"
                            :key="s.code"
                            :value="s.code"
                          >{{ s.name }}</option>
                        </select>
                      </div>
                      <div class="v2-support-modal__field v2-support-modal__field--date">
                        <label for="v2sm-date">Event date</label>
                        <input
                          id="v2sm-date"
                          ref="eventDateRef"
                          v-model="form.event_date"
                          type="date"
                          :min="today"
                          :aria-invalid="!!errors.event_date"
                        />
                      </div>
                    </div>
                    <p v-if="errors.event_date" class="v2-support-modal__err">{{ errors.event_date }}</p>

                    <!-- Agreement row — visually anchored to the form via a
                         hairline divider above. Reads as "I agree to everything
                         above" because it sits inside the form column, not
                         alongside the buttons. -->
                    <div class="v2-support-modal__agreement-row">
                      <label
                        class="v2-support-modal__checkbox"
                        :class="{ 'has-error': !!errors.agreement_checked }"
                      >
                        <input type="checkbox" v-model="form.agreement_checked" />
                        <span class="v2-support-modal__checkbox-box" aria-hidden="true">
                          <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 8.5l3 3 7-7"/>
                          </svg>
                        </span>
                        <span class="v2-support-modal__checkbox-label">
                          I agree to the
                          <span class="v2-support-modal__terms-wrap">
                            <button
                              type="button"
                              class="v2-support-modal__terms-toggle"
                              :aria-expanded="showTermsDetail ? 'true' : 'false'"
                              aria-controls="v2sm-terms-pop"
                              @click.stop.prevent="showTermsDetail = !showTermsDetail"
                            >terms</button>
                            <transition name="v2sm-terms-pop">
                              <span
                                v-if="showTermsDetail"
                                id="v2sm-terms-pop"
                                class="v2-support-modal__terms-pop"
                                role="tooltip"
                                @click.stop
                              >
                                <span class="v2-support-modal__terms-pop-arrow" aria-hidden="true"></span>
                                By checking this box, you acknowledge that all funds raised will be submitted to the Side-Out Foundation (Tax I.D. 20-2510044). Dig Pink® is a registered trademark of the Side-Out Foundation. All proceeds fund Side-Out programs and stage IV clinical research.
                              </span>
                            </transition>
                          </span>
                          and that the info above is accurate.
                        </span>
                      </label>
                      <!-- Cloudflare Turnstile, interaction-only: zero height unless
                           Cloudflare needs a click (or shows an error), then it
                           appears under the agreement it verifies. Rendered
                           explicitly (see renderTurnstile) — no cf-turnstile class,
                           so the implicit scanner never auto-renders a second one. -->
                      <div
                        v-if="turnstileEnabled"
                        ref="turnstileRef"
                        class="v2-support-modal__turnstile"
                        :class="{ 'is-visible': turnstileInteractive || turnstileErrored }"
                      ></div>
                    </div>
                  </section>

                  <p v-if="topLevelError" ref="topLevelErrorRef" class="v2-support-modal__banner v2-support-modal__banner--err" role="alert">
                    {{ topLevelError }}
                  </p>
              </div></div>
              </transition>

              <transition
                :name="stepTransitionName"
                @before-enter="onStepBeforeEnter"
                @after-enter="onStepAfterEnter"
                @after-leave="onStepAfterLeave"
              >
              <div v-if="currentStep === 3" class="v2-support-modal__step-pane"><div class="v2-support-modal__outcome">
                <!-- outcome: created — your campaign is live, surface parity with legacy /sign-up-thankyou -->
                <div v-if="successData?.outcome === 'created'" class="v2-support-modal__outcome-card">
                  <div class="v2sm-outcome-avatar">
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <span class="v2sm-outcome-eyebrow">You're Live</span>
                  <h3>Your campaign is live!</h3>
                  <p>
                    Your supporting page rolls up to <strong>{{ parentCampaign.name }}</strong>.
                    Personalize it with your story, photos, and video — then share it to start raising funds.
                  </p>
                  <a
                    :href="`/edit-campaign/${successData.campaign_id}`"
                    class="v2-support-modal__btn v2-support-modal__btn--primary"
                  >
                    Customize my page →
                  </a>
                  <div class="v2sm-outcome-secondary-row">
                    <a :href="successData.campaign_url" class="v2sm-outcome-link">View your page</a>
                    <span class="v2sm-outcome-link-divider" aria-hidden="true">·</span>
                    <a href="/active-campaigns" class="v2sm-outcome-link">Manage campaigns</a>
                  </div>
                </div>

                <!-- outcome: magic_link_sent — check inbox to finish -->
                <div v-else-if="successData?.outcome === 'magic_link_sent'" class="v2-support-modal__outcome-card">
                  <div class="v2sm-outcome-avatar">
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <span class="v2sm-outcome-eyebrow">Check Your Email</span>
                  <h3>Check your email</h3>
                  <p>We sent a sign-in link to <strong>{{ successData.email }}</strong>. Click it to publish your campaign — we've saved everything you entered here.</p>
                  <button type="button" class="v2-support-modal__btn v2-support-modal__btn--primary" @click="close">Got it</button>
                </div>

                <!-- outcome: already_registered — duplicate campaign -->
                <div v-else-if="successData?.outcome === 'already_registered'" class="v2-support-modal__outcome-card">
                  <div class="v2sm-outcome-avatar">
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <span class="v2sm-outcome-eyebrow">Welcome Back</span>
                  <h3>You already have a campaign here</h3>
                  <p><strong>{{ successData.campaign_name }}</strong> is already raising funds for this parent campaign.</p>
                  <a :href="successData.manage_url" class="v2-support-modal__btn v2-support-modal__btn--primary">Manage your campaign →</a>
                </div>

                <!-- outcome: registration_required — finish account setup -->
                <div v-else-if="successData?.outcome === 'registration_required'" class="v2-support-modal__outcome-card">
                  <div class="v2sm-outcome-avatar">
                    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <span class="v2sm-outcome-eyebrow">Almost There</span>
                  <h3>Finish creating your account</h3>
                  <p>One last step — set up your account so we can publish your campaign. We've saved your details.</p>
                  <a :href="successData.registration_url" class="v2-support-modal__btn v2-support-modal__btn--primary">Create your account →</a>
                </div>
              </div></div>
              </transition>
            </div>

            <!-- Action area: agreement checkbox stacked directly above the
                 primary CTA so the eye reads "agree → submit" in one beat.
                 No border-top — the form column flows continuously into the
                 actions instead of being chopped into "form" and "footer"
                 regions. The whole block hides on Step 1 while InlineAuth is
                 showing (that form owns its own primary button) and on Step
                 3 (outcome cards own their own CTAs). -->
            <div v-if="currentStep < 3 && !inlineAuthOwnsAdvance" class="v2-support-modal__actions">
              <!-- Agreement checkbox used to live here; it now sits at the
                   bottom of the Event section so it reads as the form's
                   closing row, not as a button-bar label. -->
              <div class="v2-support-modal__buttons">
                <button
                  v-if="currentStep === 2"
                  type="button"
                  class="dw-back-pill dw-back-pill--lg"
                  @click="goToPreviousStep"
                >
                  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10 4l-4 4 4 4"/>
                  </svg>
                  Back
                </button>
                <button
                  v-if="currentStep === 1"
                  type="button"
                  class="v2-support-modal__btn v2-support-modal__btn--primary"
                  @click="goToNextStep"
                >
                  Next
                </button>
                <button
                  v-else-if="currentStep === 2"
                  type="button"
                  class="v2-support-modal__btn v2-support-modal__btn--primary"
                  :disabled="submitting"
                  @click="onSubmit"
                >
                  {{ submitting ? 'Creating…' : 'Create Campaign' }}
                </button>
              </div>
            </div>
          </section>

          <aside class="v2-support-modal__hero-col">
            <V2SupportModalHero :step="currentStep" :steps-override="heroStepsOverride" />
          </aside>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { acquirePageScrollLock } from '../../../../composables/usePageScrollLock'
import V2SupportModalHero from './V2SupportModalHero.vue'

/**
 * V2SupportModal — split-screen takeover modal that walks a visitor
 * through creating a supporting (child) campaign without leaving the
 * v2 parent campaign page. Mirrors the /create-my-campaign/{parentId}
 * wizard's 4 steps (Email+Auth, Details, Event+Submit, Outcome) as a
 * Vue component so the parent page remains visible behind the backdrop.
 *
 * Triggered via the global mitt emitter by V2SupportOnboarding's CTA.
 * See design: docs/superpowers/specs/2026-04-24-v2-support-takeover-design.md
 */
// Prompts shown instead of submitting while Turnstile hasn't produced a token.
const TURNSTILE_PROMPTS = [
  'Please complete the security check above, then create your campaign.',
  'Still checking that you’re human — try again in a moment.',
]

// Cloudflare error families (developers.cloudflare.com/turnstile/troubleshooting/
// client-side-errors): 100xxx initialization, 110xxx configuration or unsupported
// browser (except the 1106xx timeouts), and 200xxx loading/clock problems mean the
// widget can't run here; 300xxx/600xxx challenge failures and timeouts retry.
function turnstileCannotRun(code) {
  const n = Number(code)
  if (!Number.isFinite(n)) return false
  const family = Math.floor(n / 1000)
  if (family === 110) return n < 110600 || n >= 110700
  return family === 100 || family === 200
}

const STEP_HEADINGS = {
  1: 'Start your supporting campaign',
  2: 'Tell us about your campaign',
  // Step 3's heading text is suppressed via v-if on the <h2> — the
  // success card carries its own ("Your campaign is live!"). Kept as
  // a fallback in case a future outcome shape needs a modal heading.
  3: 'All set',
}

export default {
  name: 'V2SupportModal',
  // The registration link (V2RegistrationLink) lives in the in-page support
  // section (and the dev-only Support hub), not inside signup (Brian 2026-09-27):
  // a visitor already here doesn't need it, and it pushed the form into a scrollbar.
  components: { V2SupportModalHero },
  props: {
    parentCampaign: { type: Object, required: true },
    open: { type: Boolean, required: true },
    /**
     * Optional per-step hero override sourced from the foundation's
     * template content (Support Modal editor section). 3-slot array
     * of { image, caption } — empty fields fall through to defaults
     * inside V2SupportModalHero.
     */
    heroStepsOverride: { type: Array, default: null },
  },
  emits: ['close', 'campaign-created'],
  data() {
    return {
      currentStep: 1,
      submitting: false,
      errors: {},
      authState: 'new', // 'logged_in' | 'exists_signed_in' | 'new'
      emailStatus: 'idle', // populated in Task 4
      form: {
        email: '',
        name: '',
        business_name: '',
        campaign_type: '',
        campaign_type_city: '',
        campaign_type_state: '',
        campaign_type_street: '',
        zip_code: '',
        goal: '2500',
        goal_other: '',
        event_date: '',
        agreement_checked: false,
      },
      successData: null,
      topLevelError: null,
      // Step 2's Turnstile widget: token from its callback, and whether
      // Cloudflare is currently asking for a click (widget visible).
      turnstileToken: null,
      turnstileInteractive: false,
      turnstileErrored: false,
      previouslyFocusedEl: null,
      // Starts false so InlineAuth's watch.show fires when we flip it to true
      // on nextTick — otherwise the component mounts with show=true but the
      // watcher only reacts to changes, leaving its internal isVisible=false
      // and the form hidden behind an empty <div class="inline-auth-container">.
      showInlineAuthWidget: false,
      // Toast banner for inline-auth success ("Signed in" / "Account created")
      // — displayed near the top of the panel, fades after ~2.4s.
      toastMessage: '',
      // Inline disclosure for the legal terms — revealed when the user
      // clicks "terms" in the agreement checkbox label. Reset on modal
      // open so the next visitor doesn't see the prior session's state.
      showTermsDetail: false,
      // Direction the next step transition should slide. 'forward' (right→
      // left) on advance, 'backward' (left→right) on Back. Set BEFORE we
      // mutate currentStep so the <transition> picks up the right name on
      // its first reactive render. Defaults to 'forward' so the initial
      // mount uses a forward enter (no animation since panel-in covers it).
      transitionDirection: 'forward',
    }
  },
  computed: {
    stepHeadingId() { return 'support-modal-step-heading' },
    currentHeading() {
      return this.successData?.outcomeLabel || STEP_HEADINGS[this.currentStep]
    },
    goalPresets() { return [1500, 2500, 5000, 10000] },
    /**
     * USPS-ordered list of all 50 states + DC. Drives the State <select>
     * on Step 2; the bound value is the 2-letter postal code (preserves
     * the existing form.campaign_type_state column shape that previously
     * held a free-text 2-char input). Native <select> renders as a wheel
     * picker on iOS/Android — better UX than any custom dropdown.
     */
    usStates() {
      // Source list stays code-first so future migrations to a shared
      // states dataset can drop in cleanly. Sorted alphabetically by
      // full name for the dropdown order — matches the recent precision-
      // enrollment change (commit 90476e50) so the two modals read the
      // same. Form payload still carries the 2-letter code.
      return [
        { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
        { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' },
      ].sort((a, b) => a.name.localeCompare(b.name))
    },
    today() {
      const d = new Date()
      return d.toISOString().split('T')[0]
    },
    turnstileEnabled() {
      return !!this.turnstileSiteKey
    },
    // Step 2's Turnstile slot is in the DOM exactly when this is true.
    turnstileSlotOpen() {
      return this.open && this.currentStep === 2 && this.turnstileEnabled
    },
    turnstileSiteKey() {
      const meta = document.querySelector('meta[name="turnstile-site-key"]')
      return meta ? meta.content : ''
    },
    /**
     * On Step 1, InlineAuth's own button (Sign In & Continue /
     * Create Account & Continue) is the primary advance action —
     * its auth-success handler auto-jumps to Step 2. While that
     * form is rendered, hiding the modal's Next button avoids two
     * competing primary CTAs.
     */
    inlineAuthOwnsAdvance() {
      return this.currentStep === 1
        && this.showInlineAuthWidget
        && this.authState !== 'exists_signed_in'
    },
    /**
     * Direction-aware transition name. Vue resolves the appropriate
     * enter/leave classes from the matching CSS rules below — forward
     * slides in from the right, backward from the left. Mirrors the
     * MakeDonationWidget's enterFromDirection/leaveToDirection pattern.
     */
    stepTransitionName() {
      return this.transitionDirection === 'backward' ? 'v2sm-step-back' : 'v2sm-step-fwd'
    },
  },
  watch: {
    open(nowOpen) {
      if (nowOpen) {
        this.resetState()
        this.bootstrapAuthState()
        this.$nextTick(() => {
          if (this._emailLookupClosed) return
          this.captureFocusAndTrap()
          // Hide everything except the modal from screen readers + tab
          // order. The modal lives inside the page wrapper (e.g.
          // div.bg-white inside #app), so we can't simply inert a
          // fixed top-level ancestor. Instead, walk up from the modal
          // and mark each ancestor's *siblings* inert. That keeps the
          // modal's ancestor chain live while making the rest of the
          // page untabbable and hidden from assistive tech.
          const modalEl = this.$el
          if (!modalEl) return
          this._inertedEls = []
          let node = modalEl
          while (node && node !== document.body && node.parentElement) {
            for (const sib of node.parentElement.children) {
              if (sib === node) continue
              // Skip <script>/<style>-ish non-interactive tags.
              const tag = sib.tagName
              if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK') continue
              this._inertedEls.push({ el: sib, ariaHidden: sib.getAttribute('aria-hidden'), inert: sib.inert })
              sib.setAttribute('aria-hidden', 'true')
              if ('inert' in sib) sib.inert = true
            }
            node = node.parentElement
          }
        })
        if (!this._releasePageScroll) this._releasePageScroll = acquirePageScrollLock()
        document.addEventListener('keydown', this.onKeydown)
      } else {
        this._emailLookupClosed = true
        this.invalidateEmailLookup()
        this._releasePageScroll?.()
        this._releasePageScroll = null
        document.removeEventListener('keydown', this.onKeydown)
        this.restoreInertSiblings()
        this.restoreFocus()
      }
    },
    // Step 2 fits its pane with little spare room, so an error banner (the last
    // child) would otherwise land below the fold with no visible reason.
    topLevelError(message) {
      if (message) this.$nextTick(() => this.$refs.topLevelErrorRef?.scrollIntoView?.({ block: 'nearest' }))
    },
    // Turnstile lives on Step 2, rendered fresh each time the step opens so
    // the token is young at submit. flush: 'post' runs after the step's DOM
    // exists; the old pre-flush currentStep watcher read $refs too early and
    // never mounted the widget, so every submit skipped verification.
    turnstileSlotOpen: {
      flush: 'post',
      handler(slotOpen) {
        if (slotOpen) this.loadTurnstile()
        else this.removeTurnstile()
      },
    },
    /**
     * Click-outside dismissal for the terms tooltip. Listener only
     * exists while the popover is open. Both the toggle button and the
     * popover itself stop propagation, so any click that reaches
     * document is genuinely "outside" — close the popover.
     */
    showTermsDetail(open) {
      if (open) {
        this._termsClickAway = () => { this.showTermsDetail = false }
        // Wait one tick before binding so the click that *opened* the
        // popover doesn't immediately close it. The toggle button and
        // popover both have @click.stop so any click that reaches
        // document afterward is genuinely "outside" — close.
        this.$nextTick(() => {
          if (this.showTermsDetail) {
            document.addEventListener('click', this._termsClickAway)
          }
        })
      } else if (this._termsClickAway) {
        document.removeEventListener('click', this._termsClickAway)
        this._termsClickAway = null
      }
    },
  },
  mounted() {
    // Preview-driven step navigation. The admin template editor emits this
    // when the admin clicks a step tab in SupportModalSectionEditor, so the
    // preview iframe can show that step's hero without authenticating
    // through the wizard. The handler is a no-op for normal visitors —
    // the public CTAs never emit this event.
    this.emitter?.on('support-modal:set-step', this.onSetStepFromPreview)
  },
  beforeUnmount() {
    this._emailLookupClosed = true
    this.invalidateEmailLookup()
    this.restoreInertSiblings()
    this._releasePageScroll?.()
    this._releasePageScroll = null
    document.removeEventListener('keydown', this.onKeydown)
    this.emitter?.off('support-modal:set-step', this.onSetStepFromPreview)
    if (this._toastTimer) clearTimeout(this._toastTimer)
    this.removeTurnstile()
  },
  methods: {
    loadTurnstile() {
      if (typeof window.turnstile !== 'undefined') {
        this.renderTurnstile()
        return
      }
      this._turnstileLoading = true
      clearTimeout(this._turnstileLoadTimer)
      this._turnstileLoadTimer = setTimeout(() => this.markTurnstileUnavailable(), 15000)
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        // Signed-in visitors skip Step 1, where InlineAuth would load it.
        const script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoadSignup'
        script.async = true
        script.onerror = () => this.markTurnstileUnavailable()
        window.onTurnstileLoadSignup = () => this.renderTurnstile()
        document.head.appendChild(script)
        return
      }
      // Another component injected the script; wait for it.
      clearInterval(this._turnstilePoll)
      this._turnstilePoll = setInterval(() => {
        if (typeof window.turnstile === 'undefined') return
        clearInterval(this._turnstilePoll)
        this.renderTurnstile()
      }, 100)
    },
    // Blocked script, unsupported browser, or a Cloudflare error that means it
    // can't run here: VerifyTurnstile's non-strict mode lets these through.
    markTurnstileUnavailable() {
      this._turnstileUnavailable = true
      this._turnstileLoading = false
      clearTimeout(this._turnstileLoadTimer)
      clearInterval(this._turnstilePoll)
      this.turnstileErrored = false
      this.settleTurnstileWait(null)
    },
    renderTurnstile() {
      const el = this.$refs.turnstileRef
      if (!this.turnstileSlotOpen || !el || typeof window.turnstile === 'undefined') return
      if (this._turnstileWidgetId != null || el.childElementCount > 0) return
      this._turnstileLoading = false
      clearTimeout(this._turnstileLoadTimer)
      this.turnstileToken = null
      this._turnstileWidgetId = window.turnstile.render(el, {
        sitekey: this.turnstileSiteKey,
        appearance: 'interaction-only',
        theme: 'light',
        // The 300px widget overflows 320px phones' content box.
        size: el.clientWidth && el.clientWidth < 300 ? 'compact' : 'normal',
        callback: token => {
          this.turnstileToken = token
          this.turnstileErrored = false
          if (TURNSTILE_PROMPTS.includes(this.topLevelError)) this.topLevelError = null
          this.settleTurnstileWait(token)
        },
        'expired-callback': () => { this.turnstileToken = null },
        'error-callback': code => {
          this.turnstileToken = null
          if (turnstileCannotRun(code)) {
            this.markTurnstileUnavailable()
            return
          }
          // Challenge failures and timeouts: Cloudflare retries; keep blocking.
          this.turnstileErrored = true
          this.settleTurnstileWait(null)
        },
        'unsupported-callback': () => this.markTurnstileUnavailable(),
        'before-interactive-callback': () => {
          this.turnstileInteractive = true
          this.settleTurnstileWait(null)
        },
        'after-interactive-callback': () => { this.turnstileInteractive = false },
      })
    },
    settleTurnstileWait(token) {
      const settle = this._turnstileWaiter
      this._turnstileWaiter = null
      settle?.(token)
    },
    /**
     * What Step 2's submit can prove. VerifyTurnstile (non-strict on this
     * route) lets turnstile_available=0 through, so claim 0 only when the
     * widget truly can't run (disabled, script blocked, unsupported browser).
     * Once it rendered, always claim 1: wait briefly for the invisible
     * challenge's token, and ask for the click if Cloudflare wants one.
     */
    async awaitTurnstile() {
      if (!this.turnstileEnabled) return { available: false }
      const deadline = Date.now() + 8000
      // Our own api.js may still be loading (signed-in visitors skip InlineAuth).
      while (this._turnstileLoading && this._turnstileWidgetId == null && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      if (this._turnstileUnavailable) return { available: false }
      if (this._turnstileWidgetId == null) return this._turnstileLoading ? { pending: true } : { available: false }
      if (this.turnstileToken) return { available: true, token: this.turnstileToken }
      if (!this.turnstileInteractive && !this.turnstileErrored) {
        const token = await new Promise(resolve => {
          const timer = setTimeout(() => this.settleTurnstileWait(null), Math.max(0, deadline - Date.now()))
          this._turnstileWaiter = t => { clearTimeout(timer); resolve(t) }
        })
        if (token) return { available: true, token }
      }
      if (this._turnstileUnavailable) return { available: false }
      if (this.turnstileInteractive || this.turnstileErrored) return { needsClick: true }
      return { pending: true }
    },
    // Tokens are single-use: after a submit reaches the server, get a new one.
    resetTurnstile() {
      this.turnstileToken = null
      this.turnstileInteractive = false
      this.turnstileErrored = false
      if (this._turnstileWidgetId == null || typeof window.turnstile === 'undefined') return
      try { window.turnstile.reset(this._turnstileWidgetId) } catch (e) { /* widget already gone */ }
    },
    removeTurnstile() {
      clearInterval(this._turnstilePoll)
      clearTimeout(this._turnstileLoadTimer)
      this._turnstileLoading = false
      if (this._turnstileWidgetId != null && typeof window.turnstile !== 'undefined') {
        try { window.turnstile.remove(this._turnstileWidgetId) } catch (e) { /* widget already gone */ }
      }
      this._turnstileWidgetId = null
      this._turnstileUnavailable = false
      this.turnstileToken = null
      this.turnstileInteractive = false
      this.turnstileErrored = false
      this.settleTurnstileWait(null)
    },
    restoreInertSiblings() {
      if (!this._inertedEls) return
      this._inertedEls.forEach(({ el, ariaHidden, inert }) => {
        if (ariaHidden === null) el.removeAttribute('aria-hidden')
        else el.setAttribute('aria-hidden', ariaHidden)
        if ('inert' in el) el.inert = inert
      })
      this._inertedEls = null
    },
    close() {
      // Stop async work before the parent has applied the closed prop.
      this._emailLookupClosed = true
      this.invalidateEmailLookup()
      this.$emit('close')
    },
    onBackdropClick() { this.close() },
    invalidateEmailLookup() {
      this._emailRequestVersion = (this._emailRequestVersion || 0) + 1
      clearTimeout(this._emailDebounce)
      clearTimeout(this._emailFocusTimer)
      this._emailDebounce = null
      this._emailFocusTimer = null
      this._pendingEmail = null
      this._checkedEmail = null
      return this._emailRequestVersion
    },
    isCurrentEmailLookup(email, version) {
      return this.open && !this._emailLookupClosed && this.currentStep === 1
        && this.form.email === email && this._emailRequestVersion === version
    },
    /**
     * Debounced from @input — kicks off the existence check 500ms after the
     * user stops typing, so by the time they blur the input the form has
     * usually already revealed itself. Without this, the user types, blurs,
     * then waits for a noticeable round-trip before login/registration
     * appears — feels laggy.
     */
    onEmailInput() {
      const version = this.invalidateEmailLookup()
      // Reset state if the email has been edited since the last check
      this.emailStatus = 'idle'
      this.showInlineAuthWidget = false
      // Only debounce-fire if the address looks complete enough to bother
      // (has @ and a dot in the domain part)
      const email = this.form.email
      if (!this.isCurrentEmailLookup(email, version) || !email || !/^\S+@\S+\.\S+$/.test(email)) return
      this._emailDebounce = setTimeout(() => {
        if (this.isCurrentEmailLookup(email, version)) this.checkEmailExists()
      }, 500)
    },
    async checkEmailExists() {
      // Blur can run before the input debounce. It owns the check now.
      clearTimeout(this._emailDebounce)
      this._emailDebounce = null
      const email = this.form.email
      if (!this.open || this._emailLookupClosed || this.currentStep !== 1 || !email) return
      // Moving focus into InlineAuth blurs the email again; do not repeat
      // the lookup for the same pending or already checked address.
      if (this._pendingEmail === email || this._checkedEmail === email) return
      const version = this.invalidateEmailLookup()
      this._pendingEmail = email
      this.emailStatus = 'checking'
      this.errors = { ...this.errors, email: null }
      // Reset the widget flag so the next "exists" result re-triggers the
      // false→true transition InlineAuth's watch.show depends on.
      this.showInlineAuthWidget = false
      try {
        const response = await window.axios.post('/check-email-exists', { email })
        if (!this.isCurrentEmailLookup(email, version)) return
        this._pendingEmail = null
        this._checkedEmail = email
        // The existing endpoint returns { exists: true|false, is_valid: ... }
        if (response.data.exists) {
          this.emailStatus = 'exists'
        } else {
          this.emailStatus = 'new'
          this.authState = 'new'
        }
        // For BOTH 'exists' (login form) and 'new' (registration form), Vue
        // mounts <inline-auth> now (v-if true) with :show=false. On the next
        // tick, flip the flag so the watcher inside InlineAuth fires and sets
        // its internal isVisible=true, revealing whichever form userExists selects.
        this.$nextTick(() => {
          if (this.isCurrentEmailLookup(email, version)
            && (this.emailStatus === 'exists' || this.emailStatus === 'new')) {
            this.showInlineAuthWidget = true
            // After the InlineAuth slide-fade settles, move focus into its
            // first input — that's where the user's Tab/keyboard intent
            // lives. Without this, Tab fires email's blur, the form mounts
            // ~300ms later, and focus has already jumped to whatever was
            // next in the prior DOM (Next button) — confusing.
            this.$nextTick(() => {
              if (this.isCurrentEmailLookup(email, version)) this.focusInlineAuthFirstInput(email, version)
            })
          }
        })
      } catch (err) {
        if (!this.isCurrentEmailLookup(email, version)) return
        this._pendingEmail = null
        // eslint-disable-next-line no-console
        console.warn('check-email-exists failed; continuing without hint', err)
        this.emailStatus = 'idle'
      }
    },
    /**
     * Move focus into InlineAuth's first input once it has mounted +
     * its internal slide-fade has had a frame to start. The login form
     * exposes #inline-password; the registration form starts with
     * #inline-name. We retry briefly because InlineAuth flips its
     * isVisible flag inside its own watch.show → nextTick, which
     * happens AFTER our nextTick, so the input may not exist yet on
     * the first try.
     */
    focusInlineAuthFirstInput(email, version) {
      clearTimeout(this._emailFocusTimer)
      const targetId = this.emailStatus === 'exists' ? 'inline-password' : 'inline-name'
      let attempts = 0
      const tryFocus = () => {
        this._emailFocusTimer = null
        if (!this.isCurrentEmailLookup(email, version) || !this.showInlineAuthWidget) return
        attempts++
        const el = this.$refs.panelRef?.querySelector(`[id="${targetId}"]`)
        if (el) { el.focus(); return }
        if (attempts < 6) this._emailFocusTimer = setTimeout(tryFocus, 50)
      }
      tryFocus()
    },
    onInlineAuthSuccess(user, action) {
      this.authState = 'exists_signed_in'
      this.emailStatus = 'exists'
      // Auto-advance: after the user has either signed in (login) or
      // registered, there's nothing more to do on Step 1. Push them
      // forward to Step 2 so they don't have to click Next themselves.
      // A brief toast confirms what happened ("Signed in" / "Account
      // created") before fading after a couple seconds.
      const isRegister = action === 'register'
      this.toastMessage = isRegister ? 'Account created!' : 'Signed in!'
      this.transitionDirection = 'forward'
      this.currentStep = 2
      // Auto-dismiss the toast after 2.4s
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMessage = '' }, 2400)
      // Laravel regenerates the session (+ CSRF token) on login and on
      // registration. Pre-refresh the axios default header now so the
      // eventual submit to /store-campaign-modal doesn't 419. Without
      // this, the first submit fails with 419 and the error handler
      // recovers — but that's a wasted round-trip.
      window.axios.get('/refresh-csrf').then(response => {
        const fresh = response.data?.token
        if (fresh) {
          window.axios.defaults.headers.common['X-CSRF-TOKEN'] = fresh
          const meta = document.head.querySelector('meta[name="csrf-token"]')
          if (meta) meta.setAttribute('content', fresh)
        }
      }).catch(() => {
        // If refresh-csrf fails here, the 419 handler in handleSubmitError
        // will catch it on submit and try again.
      })
    },
    /**
     * Detect whether the visitor is already authenticated. We read
     * from window.DP.userEmail (set by the server-rendered <script>
     * block in partials.environment-settings-for-javascripts) — same
     * source the legacy Blade page uses for Auth::check() bootstrapping.
     */
    bootstrapAuthState() {
      // window.DP.user is the User object (or null) populated by
      // ProvidesScriptVariables::scriptVariables() — see the layout
      // blade. The previous lookup at window.DP.userEmail was wrong
      // and silently no-op'd, leaving every authed visitor stranded
      // on the Step 1 email form.
      const authedEmail = window.DP?.user?.email
      if (authedEmail) {
        this.authState = 'logged_in'
        this.form.email = authedEmail
        // Skip the email-auth Step 1 entirely — the visitor is already
        // signed in, so the "Welcome back, finish your details" detour
        // adds a click without adding information. Step 2 ("Tell us
        // about your campaign") is the actual work.
        this.currentStep = 2
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        // ESC dismisses the terms tooltip first if it's open — losing
        // your whole campaign form because you wanted to dismiss a
        // disclosure popover would be a catastrophic UX failure.
        if (this.showTermsDetail) {
          this.showTermsDetail = false
          return
        }
        this.close()
        return
      }
      if (e.key === 'Tab') { this.enforceFocusTrap(e) }
    },
    captureFocusAndTrap() {
      this.previouslyFocusedEl = document.activeElement
      if (this.$refs.closeBtnRef) this.$refs.closeBtnRef.focus({ preventScroll: true })
    },
    enforceFocusTrap(e) {
      const panel = this.$refs.panelRef
      if (!panel) return
      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      // If focus is currently outside the panel (e.g. page content had
      // focus when modal opened and Tab raced the captureFocusAndTrap
      // nextTick), pull it back in rather than letting Tab escape.
      if (!panel.contains(document.activeElement)) {
        e.preventDefault()
        first.focus()
        return
      }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    },
    restoreFocus() {
      const target = this.previouslyFocusedEl
      this.previouslyFocusedEl = null
      if (target && typeof target.focus === 'function' && target.isConnected !== false) {
        target.focus({ preventScroll: true })
        // The mobile opener becomes visible when the parent patches v-show.
        this.$nextTick(() => {
          if (!this.open && target.isConnected !== false && !target.closest('[inert]')) {
            target.focus({ preventScroll: true })
          }
        })
      }
    },
    resetState() {
      this._emailLookupClosed = !this.open
      this.invalidateEmailLookup()
      this.currentStep = 1
      this.submitting = false
      this.errors = {}
      this.successData = null
      this.topLevelError = null
      // Clear auth + email state so close-then-reopen doesn't show the
      // previous session's inline-auth widget or email hint. bootstrapAuthState()
      // runs right after this on `watch.open` → nowOpen and re-seeds for
      // logged-in visitors; unauth visitors see a clean Step 1.
      this.authState = 'new'
      this.emailStatus = 'idle'
      this.form.email = ''
      this.showInlineAuthWidget = false
      this.toastMessage = ''
      this.showTermsDetail = false
      this.transitionDirection = 'forward'
      if (this._toastTimer) clearTimeout(this._toastTimer)
    },
    /**
     * Goal-pill click handler. Picking a preset clears the custom-amount
     * input so the two paths can't both look "selected" at once.
     */
    selectGoalPreset(preset) {
      this.form.goal = String(preset)
      this.form.goal_other = ''
    },
    /**
     * Custom-amount input handler. Typing a number flips form.goal to
     * 'other' (which deactivates any preset pill); clearing the field
     * resets goal back to '' so the user can choose a preset cleanly.
     * v-model.number runs before this handler, so form.goal_other is
     * already the parsed number (or '' for an empty input).
     */
    onCustomGoalInput() {
      const v = this.form.goal_other
      if (typeof v === 'number' && v > 0) {
        this.form.goal = 'other'
      } else if (this.form.goal === 'other') {
        this.form.goal = ''
      }
    },
    goToNextStep() {
      if (this.currentStep === 1) {
        // Validate email syntactically; require emailStatus != 'checking'.
        if (!this.form.email || !/^\S+@\S+\.\S+$/.test(this.form.email)) {
          this.errors = { ...this.errors, email: 'Enter a valid email address.' }
          return
        }
        if (this.emailStatus === 'checking') return
      }
      // Step 2 → 3 transition is owned by onSubmit (server response). No
      // client-side gate here — the only forward step from input lands you
      // at the outcome card via a successful POST.
      if (this.currentStep < 2) {
        this.transitionDirection = 'forward'
        this.currentStep++
      }
    },
    goToPreviousStep() {
      if (this.currentStep > 1) {
        this.transitionDirection = 'backward'
        this.currentStep--
      }
    },
    /**
     * Vue transition hooks for the step panes. The body lives inside a
     * relatively-positioned stage and panes are absolute, so during the
     * forward/back slide both the leaving and entering panes are
     * simultaneously present. We pin the stage's overflow to hidden so
     * the off-stage pane doesn't leak horizontally.
     */
    onStepBeforeEnter() { this._stepStageBusy = true },
    onStepAfterEnter() { this._stepStageBusy = false },
    onStepAfterLeave() { /* hook reserved for future telemetry */ },
    /**
     * Force the wizard to a specific step. Used only by the admin
     * template-editor preview — production CTAs never call this.
     * Honoured even when the modal is closed so the next open() lands
     * on the requested step (we use it together with open-support-modal).
     */
    onSetStepFromPreview(step) {
      const target = Number(step)
      if (!Number.isInteger(target) || target < 1 || target > 3) return
      // Direction-aware so the slide animation reflects the jump if the
      // modal is currently open. Closed-state jumps still land cleanly
      // because the watch.open(true) calls resetState() which would
      // reset to step 1 — to avoid that, we delay the assign one tick
      // after open so the resetState runs first and our value sticks.
      this.transitionDirection = target >= this.currentStep ? 'forward' : 'backward'
      this.$nextTick(() => {
        this.currentStep = target
      })
    },
    async onSubmit() {
      // Client-side gate — covers every field on the consolidated Step 2.
      // Order: basics → goal → event → consent. Server validation still
      // runs and any 422 routes back here via handleSubmitError.
      const errors = {}
      if (!this.form.name) errors.name = 'Required.'
      if (!this.form.business_name) errors.business_name = 'Required.'
      if (this.form.goal === 'other' && (!this.form.goal_other || this.form.goal_other < 100)) {
        errors.goal_other = 'Enter a custom goal of $100 or more.'
      }
      if (!this.form.event_date) errors.event_date = 'Pick an event date.'
      if (!this.form.agreement_checked) errors.agreement_checked = 'Please agree to the terms.'
      if (Object.keys(errors).length > 0) {
        this.errors = { ...this.errors, ...errors }
        return
      }

      this.submitting = true
      this.topLevelError = null
      this.errors = {}

      const turnstile = await this.awaitTurnstile()
      // The visitor may have closed the modal or gone Back while we waited.
      if (!this.open || this.currentStep !== 2) {
        this.submitting = false
        return
      }
      if (turnstile.needsClick || turnstile.pending) {
        this.submitting = false
        this.topLevelError = turnstile.needsClick ? TURNSTILE_PROMPTS[0] : TURNSTILE_PROMPTS[1]
        return
      }

      const payload = {
        email: this.form.email,
        name: this.form.name,
        business_name: this.form.business_name,
        campaign_type: this.form.campaign_type,
        campaign_type_city: this.form.campaign_type_city,
        campaign_type_state: this.form.campaign_type_state,
        campaign_type_street: this.form.campaign_type_street,
        zip_code: this.form.zip_code,
        goal: this.form.goal,
        goal_other: this.form.goal === 'other' ? this.form.goal_other : null,
        event_date: this.form.event_date,
      }
      if (turnstile.token) payload['cf-turnstile-response'] = turnstile.token
      payload.turnstile_available = turnstile.available ? '1' : '0'

      try {
        const response = await window.axios.post(
          `/store-campaign-modal/${this.parentCampaign.id}`,
          payload
        )
        // A 200 with success:false is a server-side refusal (e.g. the
        // extra-registration override was spent by a concurrent request).
        // Stay put and show the message rather than advancing to a blank card.
        if (response.data && response.data.success === false) {
          this.resetTurnstile()
          this.topLevelError = response.data.message || 'Something went wrong — please try again.'
          return
        }
        this.successData = response.data
        this.transitionDirection = 'forward'
        this.currentStep = 3
        if (response.data.outcome === 'created') {
          this.$emit('campaign-created', response.data)
        }
      } catch (err) {
        this.handleSubmitError(err)
      } finally {
        this.submitting = false
      }
    },
    handleSubmitError(err) {
      const resp = err.response
      if (!resp || resp.status !== 419) this.resetTurnstile()
      if (!resp) {
        this.topLevelError = 'Network error — please try again.'
        return
      }
      if (resp.status === 422 && !Object.keys(resp.data?.errors || {}).length) {
        // Message-only 422, e.g. VerifyTurnstile's failed or missing token.
        this.topLevelError = resp.data?.message || 'Security verification failed. Please try again.'
        return
      }
      if (resp.status === 422) {
        // Auto-navigate to earliest step with an error.
        const fieldErrors = resp.data.errors || {}
        const mapped = {}
        Object.keys(fieldErrors).forEach(field => { mapped[field] = fieldErrors[field][0] })
        this.errors = mapped
        // After consolidation every input field lives on Step 2 except
        // email (Step 1). Auto-navigation collapses to a binary choice.
        const step1Fields = ['email']
        if (Object.keys(mapped).some(f => step1Fields.includes(f))) {
          this.transitionDirection = 'backward'
          this.currentStep = 1
        } else {
          // Stay on Step 2 (or arrive there if we were on Step 1) — direction
          // forward; if we're already on Step 2 the watcher won't re-fire.
          this.transitionDirection = 'forward'
          this.currentStep = 2
        }
        return
      }
      if (resp.status === 429) {
        this.topLevelError = resp.data?.message || 'Too many attempts — please try again later.'
        return
      }
      if (resp.status === 403) {
        this.topLevelError = 'Security check failed. Please complete the challenge and retry.'
        return
      }
      if (resp.status === 419) {
        // Stale CSRF — common after InlineAuth registration/login regenerates
        // the session. Fetch a fresh token, install it on the axios default
        // header AND the <meta name="csrf-token"> tag that bootstrap.js
        // seeded from, then retry once. Gated by a flag to prevent infinite
        // loops if the refresh itself fails.
        if (this._csrfRetryInFlight) {
          this.topLevelError = 'Your session refresh failed. Please reload and try again.'
          this._csrfRetryInFlight = false
          return
        }
        this._csrfRetryInFlight = true
        window.axios.get('/refresh-csrf')
          .then(response => {
            const fresh = response.data?.token
            if (fresh) {
              window.axios.defaults.headers.common['X-CSRF-TOKEN'] = fresh
              const meta = document.head.querySelector('meta[name="csrf-token"]')
              if (meta) meta.setAttribute('content', fresh)
            }
            return this.onSubmit()
          })
          .finally(() => { this._csrfRetryInFlight = false })
        return
      }
      if (resp.status === 401) {
        this.topLevelError = 'Your session expired. Please sign back in.'
        this.transitionDirection = 'backward'
        this.currentStep = 1
        this.authState = 'new'
        this.emailStatus = 'exists'
        return
      }
      // 5xx and unknown
      this.topLevelError = 'Something went wrong. Please try again.'
      // eslint-disable-next-line no-console
      console.error('modalStore submit failed', resp.status, resp.data)
    },
  },
}
</script>

<style scoped>
.v2-support-modal {
  position: fixed;
  inset: 0;
  background: rgba(29, 37, 73, 0.75);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
}
.v2-support-modal__panel {
  position: relative;
  width: min(1280px, 92vw);
  height: min(760px, 92vh);
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  font-family: 'Red Hat Text', system-ui, sans-serif;
  color: var(--sideout-navy, #1D2549);
}
/* Floating close — flat pink circle, white "×", no shadow. Mirrors
   `.v2-pem__close` in V2PrecisionEnrollmentModal.vue exactly so every
   v2 modal closes with the same affordance. Hover darkens to the
   established darker-pink token (#BC0E65); active scales slightly.
   No box-shadow anywhere on the button — flat vocabulary site-wide. */
.v2-support-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 6;
  background: #DA1884;
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 180ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-support-modal__close:hover { background: #BC0E65; transform: scale(1.06); }
.v2-support-modal__close:active { background: #BC0E65; transform: scale(0.98); }
.v2-support-modal__close:focus-visible {
  outline: 3px solid rgba(218, 24, 132, 0.4);
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .v2-support-modal__close,
  .v2-support-modal__close:hover,
  .v2-support-modal__close:active { transform: none; }
}

/* Dot stepper — three dots; active becomes a 28px green pill. Mirrors
   the donation widget's progress indicator so both modals share the
   same step-position vocabulary. */
.v2sm-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 48px 4px;
}
.v2sm-stepper__dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #E5E7EB;
  transition: background 320ms cubic-bezier(0.22, 1, 0.36, 1),
              width 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2sm-stepper__dot.is-passed { background: var(--sideout-green, #97D700); }
.v2sm-stepper__dot.is-active {
  width: 28px;
  background: var(--sideout-green, #97D700);
}
@media (prefers-reduced-motion: reduce) {
  .v2sm-stepper__dot { transition-duration: 60ms; }
}
.v2-support-modal__split {
  flex: 1;
  display: flex;
  min-height: 0;
}
.v2-support-modal__form {
  flex: 1;
  min-height: 0;
  min-width: 440px;
  max-width: 620px;
  display: flex;
  flex-direction: column;
}
.v2-support-modal__hero-col {
  flex: 1;
  min-width: 0;
  position: relative;
}
.v2-support-modal__heading {
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--sideout-navy, #1D2549);
  margin: 0;
  padding: 12px 48px 16px;
}
/* The stage is the box the steps live inside. It's relatively-positioned
   so each step pane (absolute) can stack on top of its sibling during the
   slide. overflow:hidden is critical: without it, off-stage panes leak
   horizontally during the transition. */
.v2-support-modal__steps-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
/* Each pane fills the stage and owns its own vertical scroll, so a tall
   step (like Step 1's roadmap + InlineAuth) can scroll internally without
   forcing the stage to size to the tallest content. */
.v2-support-modal__step-pane {
  position: absolute;
  inset: 0;
  overflow-y: auto;
  padding: 16px 48px 24px;
}
.v2-support-modal__placeholder {
  padding: 32px;
  border: 2px dashed var(--sideout-border, #E0E4EB);
  border-radius: 12px;
  color: var(--sideout-muted, #626884);
  text-align: center;
  font-weight: 600;
}
/* ─── Action area (was: footer) ────────────────────────── */
/* No border-top — the form column flows into the actions as one
   continuous reading surface. Stacks vertically: checkbox sits a hair
   above the buttons (proximity grouping = "agree → submit" reads as one
   gesture), buttons centered horizontally. */
.v2-support-modal__actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 12px 48px 24px;
  background: #ffffff;
}
.v2-support-modal__buttons {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

/* ─── Buttons ──────────────────────────────────────────── */
/* Primary CTA — flat solid brand-pink, no gradient, no box-shadow.
   Matches `.contact__submit` (donation widget submit) and the v2-pem
   primary button family: solid color at rest, darker shade on hover,
   transform-only "lift" — no elevation/glow vocabulary. */
.v2-support-modal__btn {
  padding: 12px 28px;
  border-radius: 9999px;
  cursor: pointer;
  border: 2px solid transparent;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: background 180ms ease, color 180ms ease,
              transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 180ms ease;
}
.v2-support-modal__btn--primary {
  background: #DA1884;
  color: #ffffff;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 28px;
}
.v2-support-modal__btn--primary:hover {
  background: #BC0E65;
  transform: translateY(-1px);
}
.v2-support-modal__btn--primary:active {
  background: #BC0E65;
  transform: translateY(0);
}
.v2-support-modal__btn--primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}
.v2-support-modal__btn:focus-visible {
  outline: 3px solid var(--sideout-navy, #1D2549);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .v2-support-modal__btn,
  .v2-support-modal__btn--primary:hover,
  .v2-support-modal__btn--primary:active { transform: none !important; }
}

.v2-support-modal__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.v2-support-modal__step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
/* Mobile-only filler image (see Step 1 markup). Hidden on desktop, where the
   right-column hero already carries the imagery; revealed in the mobile media
   query below. */
.v2-support-modal__mobile-filler { display: none; }
.v2-support-modal__welcome {
  display: flex;
  gap: 16px;
  padding: 16px 20px;
  background: #FAFDF2;
  border: 1px solid #D5EF99;
  border-radius: 12px;
  color: var(--sideout-navy, #1D2549);
}
.v2-support-modal__welcome h3 { margin: 0 0 4px; font-family: 'Red Hat Display', system-ui, sans-serif; font-size: 16px; font-weight: 700; }
.v2-support-modal__welcome p { margin: 0; font-size: 14px; line-height: 1.5; }
/* Field labels + inputs mirror the donation widget's ContactForm Step 4
   "Your details" treatment: small uppercase tracked gray label sitting
   tight on a slightly softer 9px input shell. The lead field (campaign
   name) keeps its larger conversational label via the --lead override
   below. */
.v2-support-modal__field { display: flex; flex-direction: column; gap: 5px; }
.v2-support-modal__field label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--sideout-muted, #626884);
  line-height: 1.35;
}
.v2-support-modal__field input,
.v2-support-modal__field select {
  padding: 10px 12px;
  border: 1px solid var(--sideout-border, #E0E4EB);
  border-radius: 9px;
  min-height: 46px;
  font-size: 16px;
  font-family: 'Red Hat Text', system-ui, sans-serif;
  color: var(--sideout-navy, #1D2549);
  background: #ffffff;
  transition: border-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 200ms ease,
              background 200ms ease;
}
/* Placeholder copy was rendering in the browser-default medium gray — dark
   enough that empty fields read as already filled out (Rick's note: "looks
   like the fields are filled out"). Use a clearly-light gray so placeholders
   read as hints, not entered values. opacity:1 overrides Firefox's default
   placeholder fade so the color is consistent across browsers. */
.v2-support-modal__field input::placeholder,
.v2-support-modal__input-wrap input::placeholder {
  color: #9CA3AF;
  opacity: 1;
}
/* Native select chrome on macOS/Windows reads heavy compared to the
   custom inputs — strip the OS appearance and paint our own gray
   chevron via inline-SVG background-image (matches ContactForm). */
.v2-support-modal__field select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%236B7280' stroke-width='2'><path stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 14px;
  padding-right: 32px;
}
.v2-support-modal__field input:focus,
.v2-support-modal__field select:focus {
  outline: none;
  border-color: var(--sideout-pink, #DA1884);
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.v2-support-modal__field input[aria-invalid="true"],
.v2-support-modal__field select[aria-invalid="true"] {
  border-color: #EF4444;
  background-color: #FEF2F2;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}
.v2-support-modal__err { margin: 0; color: #DC2626; font-size: 13px; }
.v2-support-modal__hint { margin: 0; font-size: 13px; color: var(--sideout-muted, #626884); }
.v2-support-modal__hint--info { color: var(--sideout-navy, #1D2549); }
.v2-support-modal__hint--success { color: #4A6900; }

/* InlineAuth is shared with other flows and carries legacy inline colors.
   Keep this treatment local to signup; !important only overrides its inline
   label/input styles so focus uses the same brand pink as the email field. */
.v2-support-modal__field :deep(.auth-fields-wrapper),
.v2-support-modal__field :deep(.inline-password-reset-container) {
  background: var(--sideout-surface, #F7F8FB);
  border: 1px solid var(--sideout-border, #E0E4EB);
  border-radius: 12px;
  margin-top: 16px;
  padding: 20px;
}
.v2-support-modal__field :deep(.section-header h3) {
  color: var(--sideout-navy, #1D2549);
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
}
.v2-support-modal__field :deep(.section-header p) {
  color: var(--sideout-muted, #626884);
  line-height: 1.5;
  margin-top: 4px;
}
.v2-support-modal__field :deep(.inline-auth-container label) {
  color: var(--sideout-muted, #626884) !important;
  font-size: 12px !important;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.v2-support-modal__field :deep(.inline-auth-container .form-input) {
  border: 1px solid var(--sideout-border, #E0E4EB) !important;
  border-radius: 9px !important;
  min-height: 46px;
  font-family: 'Red Hat Text', system-ui, sans-serif;
  color: var(--sideout-navy, #1D2549);
  background: #ffffff;
}
.v2-support-modal__field :deep(.inline-auth-container .form-input:focus) {
  border-color: var(--sideout-pink, #DA1884) !important;
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.v2-support-modal__field :deep(.code-input-container) {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
}
.v2-support-modal__field :deep(.code-digit) {
  width: 100%;
  min-width: 0;
  padding-inline: 0;
  font-family: 'Red Hat Text', system-ui, sans-serif;
  color: var(--sideout-navy, #1D2549);
}
.v2-support-modal__field :deep(.code-digit:focus:not(.error)) {
  border-color: var(--sideout-pink, #DA1884);
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.15);
}
.v2-support-modal__field :deep(.inline-auth-container button[type="submit"]) {
  min-height: 46px;
  justify-content: center;
  background: var(--sideout-pink, #DA1884);
  color: #ffffff;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 700;
  box-shadow: none;
}
.v2-support-modal__field :deep(.inline-auth-container button[type="submit"]:hover:not(:disabled)) {
  background: var(--sideout-pink-hover, #BC0E65);
}
.v2-support-modal__field :deep(.inline-auth-container button:focus-visible) {
  outline: 3px solid var(--sideout-navy, #1D2549);
  outline-offset: 3px;
  box-shadow: none;
}
.v2-support-modal__field :deep(.login-section form > .flex) {
  flex-wrap: wrap;
  gap: 12px;
}

/* ─── Inline-auth success toast ────────────────────────── */
.v2-support-modal__toast {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--sideout-green, #97D700);
  color: var(--sideout-navy, #1D2549);
  padding: 10px 18px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 8px 24px -8px rgba(29, 37, 73, 0.2);
  z-index: 10;
  pointer-events: none;
}
.v2-support-modal-toast-enter-active,
.v2-support-modal-toast-leave-active {
  transition: opacity 220ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2-support-modal-toast-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
.v2-support-modal-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}
@media (prefers-reduced-motion: reduce) {
  .v2-support-modal-toast-enter-active,
  .v2-support-modal-toast-leave-active { transition-duration: 60ms; }
}

/* ─── Step 1 intro + roadmap ───────────────────────────── */
/* Uses the modern grid-template-rows 1fr/0fr collapse trick: the
   wrapper animates from "content height" → 0, so there's no fixed
   max-height cap to mis-tune (the prior approach jumped because
   320px wasn't the real content size). Inner overflow:hidden +
   min-height:0 let the grid row actually collapse. */
.v2-support-modal__lead-wrap {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 200ms ease 60ms;
  opacity: 1;
}
.v2-support-modal__lead-wrap > .v2-support-modal__lead {
  overflow: hidden;
  min-height: 0;
}
.v2-support-modal__lead-wrap.is-collapsed {
  grid-template-rows: 0fr;
  opacity: 0;
  transition: grid-template-rows 280ms cubic-bezier(0.4, 0, 0.2, 1),
              opacity 160ms ease;
}
.v2-support-modal__lead {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
@media (prefers-reduced-motion: reduce) {
  .v2-support-modal__lead-wrap,
  .v2-support-modal__lead-wrap.is-collapsed {
    transition-duration: 60ms;
  }
}

.v2-support-modal__intro {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--sideout-muted, #626884);
}
.v2-support-modal__intro strong {
  color: var(--sideout-navy, #1D2549);
  font-weight: 700;
}
.v2-support-modal__roadmap {
  list-style: none;
  margin: 0;
  padding: 14px 16px;
  background: var(--sideout-surface, #F7F8FB);
  border: 1px solid var(--sideout-border, #E0E4EB);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.v2-support-modal__roadmap-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.v2-support-modal__roadmap-num {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: var(--sideout-pink, #DA1884);
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.v2-support-modal__roadmap-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-family: 'Red Hat Text', system-ui, sans-serif;
}
.v2-support-modal__roadmap-text strong {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--sideout-navy, #1D2549);
  line-height: 1.2;
}
.v2-support-modal__roadmap-text small {
  font-size: 12px;
  color: var(--sideout-muted, #626884);
  line-height: 1.35;
}

/* Desktop: bump the roadmap card type so the "what's next" preview
   reads like a confident promise rather than fine print. Keeps the
   mobile sizing untouched (the card is already proportional there). */
@media (min-width: 1024px) {
  /* Intro paragraph reads as the modal's opening sentence — bump it
     up on desktop so the typographic hierarchy (intro 16/1.55 →
     roadmap card 16/1.2 → field labels 11/1) feels intentional rather
     than uniformly small. Mobile stays 14px to keep the panel
     compact. */
  .v2-support-modal__intro {
    font-size: 16px;
    line-height: 1.55;
  }
  .v2-support-modal__roadmap {
    padding: 18px 20px;
    gap: 14px;
  }
  .v2-support-modal__roadmap-num {
    width: 26px;
    height: 26px;
    font-size: 13px;
  }
  .v2-support-modal__roadmap-text strong {
    font-size: 16px;
  }
  .v2-support-modal__roadmap-text small {
    font-size: 14px;
  }
}

/* ─── Email input + inline spinner ─────────────────────── */
.v2-support-modal__input-wrap {
  position: relative;
}
.v2-support-modal__input-wrap input {
  width: 100%;
  /* extra right padding so the spinner doesn't sit on top of the text */
  padding-right: 38px;
}
.v2-support-modal__spinner {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  border: 2px solid var(--sideout-border, #E0E4EB);
  border-top-color: var(--sideout-pink, #DA1884);
  animation: v2-support-modal-spin 700ms linear infinite;
}
@keyframes v2-support-modal-spin {
  to { transform: translateY(-50%) rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .v2-support-modal__spinner { animation-duration: 1.4s; }
}

.v2-support-modal__field-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.v2-support-modal__field--grow { flex: 1; min-width: 0; }
.v2-support-modal__field--type { width: 150px; }

/* ─── Step 2 (Tell us about it) — editorial sections ───── */
/* Density rule: at this rhythm (16px between sections; the agreement row
   closes the Event section) Step 2 needs ~743px of the min(760px, 92vh)
   panel. Shorter desktop windows switch to the laptop-height tiers at the
   end of this block, which tighten the rhythm instead of scrolling. */
.v2-support-modal__step--details {
  gap: 16px !important;
}
.v2-support-modal__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
}
.v2-support-modal__section + .v2-support-modal__section {
  padding-top: 12px;
  border-top: 1px solid var(--sideout-border, #E0E4EB);
}
.v2-support-modal__section--compact { gap: 6px; }
/* Section-level question — same visual treatment as the lead-field
   label ("What should we call your campaign?") so all of Step 2's
   conversational questions feel like one family. Sits on its own line
   below the eyebrow, matching the campaign-name section's layout. */
.v2-support-modal__section-prompt {
  font-size: 14px;
  font-weight: 600;
  color: var(--sideout-navy, #1D2549);
  line-height: 1.3;
}
/* Eyebrow uses Barlow Condensed (the established v2 exception alongside
   widget chicklets) — gives section labels a confident editorial weight
   that pairs with Red Hat Display body copy without competing. */
.v2-support-modal__eyebrow {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sideout-pink, #DA1884);
  line-height: 1;
  position: relative;
  padding-left: 16px;
}
.v2-support-modal__eyebrow::before {
  /* Pink dot left of the eyebrow — small repeated visual cue tying the
     section labels together without bordering the whole step. */
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--sideout-pink, #DA1884);
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.18);
}
/* Lead field — campaign name gets a conversational question label
   ("What should we call your campaign?") instead of the small
   uppercase noun label used for the rest of the form. The INPUT
   itself stays the same shape as Organization/Type/City/etc so the
   form reads as a uniform stack of fields with one editorial moment
   above (the question) — not a giant input competing with the
   surrounding ones. */
.v2-support-modal__field--lead label {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: var(--sideout-navy, #1D2549) !important;
  letter-spacing: normal !important;
  text-transform: none !important;
  line-height: normal !important;
}
.v2-support-modal__field-hint {
  font-size: 13px;
  color: var(--sideout-muted, #626884);
  font-weight: 500;
  margin: 0;
  line-height: 1.45;
}

/* ─── Goal pills — premium chicklets ───────────────────── */
/* Goal pills mirror the donation widget's .amount-pill exactly: 4-col
   grid (preset amounts) with Custom on its own full-width row, 6px
   radius, Red Hat Display 600 / 18px, solid pink active state (no
   gradient, no halo shadow). One radio-group vocabulary across the
   donate flow and the support flow so they read as a single product. */
.v2-support-modal__goalrow {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.v2-support-modal__pills {
  display: grid;
  /* Four choices fit at the normal desktop text size. An em-based minimum
     lets enlarged text wrap instead of making the grid wider than its pane. */
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 7em), 1fr));
  gap: 8px;
}
.v2-support-modal__pill {
  display: block;
  width: 100%;
  padding: 16px 12px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  color: #000000;
  background: #ffffff;
  cursor: pointer;
  transition: background-color 240ms cubic-bezier(0.22, 1, 0.36, 1),
              color 240ms cubic-bezier(0.22, 1, 0.36, 1),
              border-color 240ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
@media (min-width: 1024px) {
  .v2-support-modal__pill { padding: 8px 24px; }
}
.v2-support-modal__pill-amount {
  /* The base pill now carries the typography directly; the inner span
     just inherits. Kept as a hook in case we ever need micro-targeting. */
  font: inherit;
  color: inherit;
  letter-spacing: 0;
  line-height: 1;
}
/* Goal pills mirror the donation widget's amount-pill blue family
   (DonationForm.vue line ~766): unselected hovers into darker-blue
   border, active sits at brand-blue #009cde, active hover deepens to
   #0075a7. Same product family = same chiclet vocabulary, so the
   "Support" and "Donate" flows feel like one connected experience. */
.v2-support-modal__pill:hover {
  border-color: #0075a7;
  transform: translateY(-1px);
}
.v2-support-modal__pill:active { transform: translateY(0); }
.v2-support-modal__pill.is-active {
  background: #009cde;
  border-color: #009cde;
  color: #ffffff;
  transform: none;
}
.v2-support-modal__pill.is-active:hover {
  background: #0075a7;
  border-color: #0075a7;
  color: #ffffff;
  transform: none;
}
/* Custom amount pill — visually a sibling of the preset pills (same
   border, radius, font, height) but with a leading $ glyph and an
   editable number where the dollar text would be. Lives in row 2 of
   the pill grid via grid-column: 1 / -1, so it spans the full row. */
.v2-support-modal__custom-pill {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px 12px;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  cursor: text;
  transition: border-color 240ms cubic-bezier(0.22, 1, 0.36, 1),
              box-shadow 240ms ease,
              transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
@media (min-width: 1024px) {
  .v2-support-modal__custom-pill { padding: 8px 16px; }
}
/* Custom amount pill matches the preset pill family (blue) so when
   the user clicks into the input the focus ring also reads blue and
   the active border picks up the same brand-blue. Glyph follows suit. */
.v2-support-modal__custom-pill:hover {
  border-color: #0075a7;
}
.v2-support-modal__custom-pill:focus-within {
  border-color: #009cde;
  box-shadow: 0 0 0 3px rgba(0, 156, 222, 0.18);
  outline: none;
}
.v2-support-modal__custom-pill.is-active {
  border-color: #009cde;
}
.v2-support-modal__custom-pill-glyph {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 18px;
  font-weight: 700;
  /* Rick 2026-06-06: the custom "$" amount read "too light (low opacity)" —
     it was a faint #9CA3AF gray until the field went active. Anchor it at solid
     brand navy so the dollar glyph is fully opaque at rest; is-active still
     swaps it to brand blue to echo the focused field. */
  color: var(--sideout-navy, #1D2549);
  line-height: 1;
  padding-left: 4px;
  flex: 0 0 auto;
}
.v2-support-modal__custom-pill.is-active .v2-support-modal__custom-pill-glyph {
  color: #009cde;
}
.v2-support-modal__custom-pill input {
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: none !important;
  padding: 0 !important;
  font-family: 'Red Hat Display', sans-serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--sideout-navy, #1D2549) !important;
  outline: none;
  box-shadow: none !important;
  border-radius: 0 !important;
}
.v2-support-modal__custom-pill input:focus {
  box-shadow: none !important;
  border-color: transparent !important;
}
.v2-support-modal__custom-pill input::placeholder {
  /* Rick 2026-06-11: the "Custom amount" prompt read "too light" on desktop +
     mobile. Darken from #9CA3AF (gray-400) to gray-600 + force opacity:1
     (Firefox dims placeholders by default) so it's clearly legible while still
     reading as a placeholder hint rather than an entered value. */
  color: #4B5563;
  font-weight: 600;
  opacity: 1;
}
.v2-support-modal__custom-pill input[aria-invalid="true"] {
  background: transparent !important;
}
/* Hide native number-input spinners — they look heavy next to the
   borderless field. */
.v2-support-modal__custom-pill input::-webkit-outer-spin-button,
.v2-support-modal__custom-pill input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.v2-support-modal__custom-pill input[type="number"] {
  -moz-appearance: textfield;
}

@media (prefers-reduced-motion: reduce) {
  .v2-support-modal__pill { transition-duration: 60ms; }
  .v2-support-modal__pill:hover,
  .v2-support-modal__pill.is-active { transform: none; }
}

/* ─── Event 3-up row (City | State | Date) ─────────────── */
/* State column went from 90px (2-letter code only) to 1.5fr so full
   state names like "District of Columbia" can read without truncation.
   minmax(0, …) lets each column shrink below content size when the
   form column is narrow rather than blowing the row's horizontal
   bounds. */
.v2-support-modal__event-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.3fr);
  gap: 10px;
  align-items: end;
}
/* Native <input type="date"> renders cramped with a plain border in some
   browsers — match the height of the sibling text inputs by giving it
   the same vertical padding and harmonising the inner control padding. */
.v2-support-modal__field--date input {
  padding-right: 8px !important;
  font-variant-numeric: tabular-nums;
}
.v2-support-modal__field--date input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 150ms ease;
  padding: 4px;
}
.v2-support-modal__field--date input:hover::-webkit-calendar-picker-indicator {
  opacity: 1;
}

/* ─── Agreement row (last form row of the Event section) ─ */
/* Lives inside the form column, not in the action bar — Rick's
   direction was that "I agree…" should feel attached to the data the
   user just filled in, not to the buttons. A hairline divider above
   marks the row as a transition: "you've filled out the form; now
   acknowledge the terms." Padding above/below matches the field
   rhythm so the row breathes inside the form column. */
.v2-support-modal__agreement-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--sideout-border, #E0E4EB);
}
/* Turnstile takes space only while Cloudflare is asking for a click or
   showing an error; otherwise the slot stays collapsed, including after the
   check passes, so Step 2 keeps its no-scroll fit. */
.v2-support-modal__turnstile:not(.is-visible) { height: 0; overflow: hidden; }
.v2-support-modal__turnstile.is-visible { margin-top: 10px; }

/* ─── Custom checkbox ──────────────────────────────────── */
/* The native checkbox is tasteless next to the rest of the form. We
   hide it (visually) and render a sibling box that paints pink on
   :checked. The .v2-support-modal__checkbox-box ::before never paints
   without :checked, so the icon stays hidden until acknowledged. */
.v2-support-modal__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--sideout-muted, #626884);
  line-height: 1.35;
  cursor: pointer;
  user-select: none;
}
.v2-support-modal__checkbox input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}
.v2-support-modal__checkbox-box {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid var(--sideout-border, #E0E4EB);
  background: #ffffff;
  color: transparent;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease;
}
/* Hover + focus visuals mirror `.contact__checkbox` in ContactForm.vue
   exactly: hover lifts the border to the lighter pink (#F0A3CE) so the
   affordance is visible but doesn't pre-commit to "selected"; focus
   adds the 3px pink halo the rest of the form uses for input focus. */
.v2-support-modal__checkbox:hover .v2-support-modal__checkbox-box {
  border-color: #F0A3CE;
}
.v2-support-modal__checkbox input:focus-visible + .v2-support-modal__checkbox-box {
  box-shadow: 0 0 0 3px rgba(218, 24, 132, 0.2);
  border-color: var(--sideout-pink, #DA1884);
}
.v2-support-modal__checkbox input:checked + .v2-support-modal__checkbox-box {
  background: var(--sideout-pink, #DA1884);
  border-color: var(--sideout-pink, #DA1884);
  color: #ffffff;
}
.v2-support-modal__checkbox.has-error .v2-support-modal__checkbox-box {
  border-color: #DC2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}
.v2-support-modal__checkbox-label { line-height: 1.35; }
.v2-support-modal__checkbox a,
.v2-support-modal__terms-toggle {
  color: var(--sideout-pink, #DA1884);
  text-decoration: underline;
  text-underline-offset: 2px;
}
/* Inline disclosure for the legal terms — kept lightweight: a button
   styled as a link that toggles the detail paragraph below. Avoids
   sending the visitor to a new tab to read three sentences. */
.v2-support-modal__terms-toggle {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.v2-support-modal__terms-toggle:hover { color: #BC0E65; }
.v2-support-modal__terms-toggle:focus-visible {
  outline: 2px solid rgba(218, 24, 132, 0.4);
  outline-offset: 2px;
  border-radius: 2px;
}
/* Terms tooltip — floats above the trigger via absolute positioning so
   the modal never has to reflow or scroll to show the disclosure. The
   wrapper provides the relative positioning context inline within the
   checkbox label sentence. */
.v2-support-modal__terms-wrap {
  position: relative;
  display: inline-block;
}
.v2-support-modal__terms-pop {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 320px;
  max-width: calc(100vw - 32px);
  padding: 12px 14px;
  background: var(--sideout-navy, #1D2549);
  color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.35);
  font-size: 12.5px;
  line-height: 1.5;
  font-weight: 500;
  text-align: left;
  z-index: 12;
  /* Allow text selection inside the tooltip — visitors may want to
     copy the Tax I.D. */
  user-select: text;
  cursor: default;
}
.v2-support-modal__terms-pop-arrow {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid var(--sideout-navy, #1D2549);
}
.v2sm-terms-pop-enter-active {
  transition: opacity 200ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2sm-terms-pop-leave-active {
  transition: opacity 140ms ease, transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2sm-terms-pop-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}
.v2sm-terms-pop-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(2px);
}
@media (prefers-reduced-motion: reduce) {
  .v2sm-terms-pop-enter-active,
  .v2sm-terms-pop-leave-active { transition-duration: 60ms; }
}
.v2-support-modal__banner {
  margin: 0;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
}
.v2-support-modal__banner--err {
  background: #FEF2F2;
  color: #991B1B;
  border: 1px solid #FECACA;
}

/* Outcome step — centered card mirroring the donation widget Step 4
   "Your details" pattern from image1.png: pink avatar circle, UPPERCASE
   eyebrow, big bold heading, supporting copy, primary pill CTA. The four
   outcomes (created / magic_link_sent / already_registered /
   registration_required) share this scaffold; their per-state copy is
   in the template above. */
.v2-support-modal__outcome {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0 32px;
  min-height: 100%;
}
.v2-support-modal__outcome-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  max-width: 420px;
  color: var(--sideout-navy, #1D2549);
}
.v2sm-outcome-avatar {
  width: 64px;
  height: 64px;
  border-radius: 9999px;
  background: #DA1884;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}
.v2sm-outcome-eyebrow {
  font-family: 'Red Hat Display', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sideout-pink, #DA1884);
  line-height: 1;
}
.v2-support-modal__outcome-card h3 {
  font-family: 'Red Hat Display', system-ui, sans-serif;
  margin: 4px 0 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
}
.v2-support-modal__outcome-card p {
  margin: 4px 0 0;
  font-size: 15px;
  line-height: 1.55;
  color: var(--sideout-muted, #626884);
  max-width: 380px;
}
.v2-support-modal__outcome-card a,
.v2-support-modal__outcome-card button {
  margin-top: 16px;
  text-decoration: none;
}
/* Secondary action row beneath the primary CTA on the `created` outcome —
   two ghost text links separated by a middle dot. Quieter visual weight
   than the primary pill, gives the user a path to View / Dashboard
   without competing with Customize as the primary next step. */
.v2sm-outcome-secondary-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.v2sm-outcome-link {
  color: var(--sideout-muted, #626884);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: color 180ms ease, border-color 180ms ease;
}
/* Defeat the parent `.v2-support-modal__outcome-card a` rule's
   margin-top: 16px — the secondary row's flex layout owns vertical
   placement, the inherited margin would push these anchors down a row
   in non-flex fallback contexts. */
.v2sm-outcome-secondary-row .v2sm-outcome-link {
  margin-top: 0;
}
.v2sm-outcome-link:hover {
  color: var(--sideout-pink, #DA1884);
  border-bottom-color: var(--sideout-pink, #DA1884);
}
/* Match the file's consistent keyboard-focus treatment (see
   .v2-support-modal__terms-toggle:focus-visible above). */
.v2sm-outcome-link:focus-visible {
  outline: 2px solid rgba(218, 24, 132, 0.4);
  outline-offset: 2px;
  border-radius: 2px;
}
.v2sm-outcome-link-divider {
  color: var(--sideout-border, #E0E4EB);
  user-select: none;
}
@media (prefers-reduced-motion: reduce) {
  .v2sm-outcome-link { transition: none; }
}

/* Open/close motion */
.v2-support-modal-enter-active, .v2-support-modal-leave-active { transition: opacity 220ms ease; }
.v2-support-modal-enter-active .v2-support-modal__panel,
.v2-support-modal-leave-active .v2-support-modal__panel {
  transition: transform 250ms cubic-bezier(0.22, 1, 0.36, 1), opacity 250ms;
}
.v2-support-modal-enter-from, .v2-support-modal-leave-to { opacity: 0; }
.v2-support-modal-enter-from .v2-support-modal__panel,
.v2-support-modal-leave-to .v2-support-modal__panel {
  transform: scale(0.96);
  opacity: 0;
}

/* ─── Compact InlineAuth inside the modal ────────────────
   InlineAuth is reused from the legacy standalone page where it has a
   whole viewport to breathe. Inside our 760px-tall modal panel the
   default padding + space-y-4 spacing overflows the step-body. Trim
   it here via :deep() so legacy usage stays untouched.
*/
.v2-support-modal__step-pane :deep(.auth-fields-wrapper) > form {
  /* Override Tailwind space-y-4 (1rem) */
  & > :not([hidden]) ~ :not([hidden]) { margin-top: 0.5rem; }
}
.v2-support-modal__step-pane :deep(.section-header) {
  margin-bottom: 0.5rem;
}
.v2-support-modal__step-pane :deep(.section-header h3) { font-size: 1rem; }
.v2-support-modal__step-pane :deep(.section-header p)  { font-size: 0.8125rem; line-height: 1.3; }
.v2-support-modal__step-pane :deep(.login-section .form-input),
.v2-support-modal__step-pane :deep(.registration-section .form-input) {
  padding: 0.5rem 0.75rem 0.5rem 2.25rem !important;
  font-size: 1rem !important;
  border-radius: 0.5rem;
}
.v2-support-modal__step-pane :deep(.login-section label),
.v2-support-modal__step-pane :deep(.registration-section label) {
  font-size: 0.75rem !important;
  margin-bottom: 0.25rem !important;
}
.v2-support-modal__step-pane :deep(.registration-section .grid) { gap: 0.5rem; }
.v2-support-modal__step-pane :deep(.registration-section .mt-2) { margin-top: 0.25rem; }
.v2-support-modal__step-pane :deep(form button[type="submit"]) {
  padding: 0.5rem 1.25rem !important;
  font-size: 0.875rem !important;
}
/* Pull the whole step column up a hair — Step 1 with the registration
   form is the tallest, and we want to avoid a 10-15px scroll tail. */
.v2-support-modal__step-pane { padding-top: 8px; padding-bottom: 16px; }
.v2-support-modal__heading { padding-bottom: 8px; }

/* ─── Step pane slide transitions (forward + back) ─────── */
/* The forward variant slides the entering pane in from the right while
   the leaving pane exits to the left (reading direction). The back
   variant mirrors it. Both use the same out-quint curve as the donation
   widget so the two surfaces feel like one product family. */
.v2sm-step-fwd-enter-active,
.v2sm-step-fwd-leave-active,
.v2sm-step-back-enter-active,
.v2sm-step-back-leave-active {
  transition: transform 480ms cubic-bezier(0.22, 1, 0.36, 1),
              opacity 280ms ease;
  will-change: transform, opacity;
}
.v2sm-step-fwd-enter-from { transform: translateX(36px); opacity: 0; }
.v2sm-step-fwd-leave-to   { transform: translateX(-36px); opacity: 0; }
.v2sm-step-back-enter-from { transform: translateX(-36px); opacity: 0; }
.v2sm-step-back-leave-to   { transform: translateX(36px); opacity: 0; }
/* The leaving pane lifts a layer above the entering one for the first
   half of the slide so its content reads as "moving aside" rather than
   "fading underneath" — small detail, big perceptual lift. */
.v2sm-step-fwd-leave-active,
.v2sm-step-back-leave-active { z-index: 2; }
.v2sm-step-fwd-enter-active,
.v2sm-step-back-enter-active { z-index: 1; }

/* ─── Heading crossfade ────────────────────────────────── */
/* Heading uses mode="out-in" so the aria-labelledby target is always
   singular (only one h2 with the heading id at any moment). The fade
   is short (180ms in / 140ms out) so it doesn't lag behind the step
   slide — the heading should resolve before the eye lands on the step body. */
.v2sm-heading-enter-active {
  transition: opacity 200ms ease, transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
.v2sm-heading-leave-active {
  transition: opacity 140ms ease, transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.v2sm-heading-enter-from { opacity: 0; transform: translateY(-6px); }
.v2sm-heading-leave-to   { opacity: 0; transform: translateY(6px); }

@media (prefers-reduced-motion: reduce) {
  .v2sm-step-fwd-enter-active,
  .v2sm-step-fwd-leave-active,
  .v2sm-step-back-enter-active,
  .v2sm-step-back-leave-active,
  .v2sm-heading-enter-active,
  .v2sm-heading-leave-active {
    transition-duration: 80ms !important;
    transform: none !important;
  }
  .v2sm-step-fwd-enter-from,
  .v2sm-step-fwd-leave-to,
  .v2sm-step-back-enter-from,
  .v2sm-step-back-leave-to,
  .v2sm-heading-enter-from,
  .v2sm-heading-leave-to {
    transform: none !important;
  }
}
/* Tighten the stacking gap between email input, hint, and the
   InlineAuth widget in Step 1. */
.v2-support-modal__field:has(#v2sm-email) + .v2-support-modal__hint,
.v2-support-modal__step > .v2-support-modal__field + * { margin-top: 6px; }

@media (prefers-reduced-motion: reduce) {
  .v2-support-modal-enter-active, .v2-support-modal-leave-active,
  .v2-support-modal-enter-active .v2-support-modal__panel,
  .v2-support-modal-leave-active .v2-support-modal__panel { transition-duration: 60ms; }
}

@media (max-width: 1023px) {
  .v2-support-modal { padding: 0; }
  .v2-support-modal__panel {
    width: 100vw;
    height: 100vh;
    /* Dynamic viewport height — tracks the *visible* area as the mobile
       URL bar shows/hides and the soft keyboard opens, so the panel never
       extends past the fold and gets centre-clipped top & bottom. The
       bare 100vh above is the large-viewport fallback for browsers
       without dvh. Mirrors V2PrecisionEnrollmentModal's mobile panel. */
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    max-width: none;
  }
  .v2-support-modal__split {
    flex-direction: column;
  }
  .v2-support-modal__form {
    order: 2;
    min-width: 0;
    max-width: none;
    width: 100%;
    /* No top buffer needed — the stepper bar below now occupies the
       row alongside the floating X (Rick's ask: progress dots inline
       with the close button, then heading, form, and action bar
       stacking cleanly below). */
    padding-top: 0;
  }
  /* Hero column hides on mobile entirely — Brian's call. Frees ~96px
     of vertical so the consolidated Step 2 fits without scroll and
     without the per-step sub-pane gymnastics. The X close button
     (absolutely positioned on the panel) still floats over the form
     column's top padding so the exit affordance never disappears. */
  .v2-support-modal__hero-col { display: none; }
  /* Rick 2026-06-06 + re-raised 2026-06-11: "add space above the title" —
     give the heading a clear, deliberate gap below the stepper row on mobile
     (was 14px; +stepper's 14px bottom now reads as ~44px of breathing room so
     the title no longer crowds the progress dots). */
  .v2-support-modal__heading { padding: 30px 20px 10px; font-size: 22px; }
  .v2-support-modal__step-pane { padding: 6px 20px 14px; }
  /* Step 1 top-aligns on mobile so the filler image pins directly under the
     heading and closes the empty gap (the generic `safe center` below would
     float the short email/sign-in form mid-pane — the reported "huge gap").
     Double-class specificity so this beats the base `.v2-support-modal__
     step-pane { justify-content: safe center }` rule declared later in this
     same media block (equal single-class specificity would let source order
     win and silently revert Step 1 to centered). */
  .v2-support-modal__step-pane.v2-support-modal__step-pane--step1 { justify-content: flex-start; }
  /* Keep the campaign photo as a compact band so the first input appears
     sooner on phones. */
  .v2-support-modal__mobile-filler {
    display: block;
    width: 100%;
    height: 128px;
    object-fit: cover;
    object-position: center 30%;
    border-radius: 14px;
  }
  /* Step 2 shares the compact band with its existing group-photo focal point. */
  .v2-support-modal__mobile-filler--step2 {
    object-position: center 35%;
  }
  /* Stepper sits inline with the floating X. X is `top:16px` + `height:32px`
     → its vertical center lands at y=32px. padding-top:27px places the
     10px-tall dots' centerline at y=32px exactly, so the dots and X read
     as one row. 56px horizontal padding keeps the centered dot cluster
     clear of the X (which lives at right:16px, 32px wide). */
  .v2sm-stepper { padding: 27px 56px 14px; }

  /* Center the form body when it fits; scroll it when it doesn't.
     -------------------------------------------------------------
     The previous mobile layout pinned the panel to 100vh and let the step
     content size the stage (relative pane + per-step `:has()` auto
     margins). That centered nicely on a full-height phone but had no
     escape hatch: the moment the *visible* viewport dropped below the
     form's ~790px natural height — URL bar showing, soft keyboard open,
     or simply a shorter / landscape phone — the over-tall column had
     nowhere to go, so the stepper and the CREATE CAMPAIGN action bar were
     clipped off-screen with no way to scroll to them. That was the
     reported bug.

     Fix: keep the same scroll architecture the desktop layout already
     relies on — the stage stays `flex: 1` (base) and the pane stays the
     absolutely-positioned scroll container (base `position: absolute;
     inset: 0; overflow-y: auto`), so the stepper, heading, and action bar
     stay pinned while only the form body between them scrolls.
     `justify-content: safe center` keeps the centered look while the body
     fits, then falls back to top-aligned scrolling once it overflows —
     the `safe` keyword guarantees the top of the form stays reachable
     (plain `center` would push it above the scroll origin and re-clip it). */
  .v2-support-modal__step-pane {
    display: flex;
    flex-direction: column;
    /* flex-start is the fallback for browsers that don't understand the
       `safe` overflow-alignment keyword (Safari < 17.4): they discard the
       `safe center` line and keep flex-start, so a too-tall form starts at
       the top and scrolls rather than centring-then-clipping the stepper. */
    justify-content: flex-start;
    justify-content: safe center;
  }
  .v2-support-modal__actions {
    padding: 6px 20px 14px;
    padding-bottom: max(14px, env(safe-area-inset-bottom));
    gap: 8px;
  }
  .v2-support-modal__field :deep(.auth-fields-wrapper),
  .v2-support-modal__field :deep(.inline-password-reset-container) { padding: 16px; }
  /* Mobile dials back the desktop spacing bump so all three sections
     (Basics / Goal / Event) plus the agreement row and action bar fit
     in 844px of viewport without a scrollbar. The lead-field hint
     also hides — its content lives on a separate page in spirit, and
     we need every vertical pixel back. */
  .v2-support-modal__step--details { gap: 10px !important; }
  .v2-support-modal__section + .v2-support-modal__section { padding-top: 4px; }
  .v2-support-modal__field--lead .v2-support-modal__field-hint { display: none; }
  /* Eyebrows + section prompts read as compact section labels in the
     mobile consolidated view — they're the only signal of where one
     section ends and the next begins, so they stay (the round-2
     sub-step revision hid them, but only worked because the heading
     swapped per section; that's no longer the case). */
  .v2-support-modal__eyebrow { margin-bottom: 2px; }
  .v2-support-modal__section-prompt { margin-bottom: 2px; }

  /* Goal pills wrap to a 2×2 grid + full-width custom amount. The
     desktop 4-up row overflows past the modal edge at 390px — the
     pill text ("$10,000" at 18px) needs ~104px and 4 columns force
     ~80px. 2×2 gives each pill ~167px, plenty for the digits and a
     comfortable thumb target. Smaller pill padding (12→10px vertical)
     buys back another ~14px of vertical run for the form below. */
  .v2-support-modal__pills {
    /* Keep at most two columns on phones; larger text can use one column. */
    grid-template-columns: repeat(auto-fit, minmax(min(100%, max(7em, 45%)), 1fr));
    gap: 6px;
  }
  .v2-support-modal__pill { padding: 10px 14px; font-size: 16px; }
  .v2-support-modal__custom-pill { padding: 10px 14px; }
  .v2-support-modal__custom-pill input { font-size: 15px; }

  /* Action row: Back is a fixed-width pill ("BACK" fits in ~92px),
     Create Campaign claims the rest. nowrap prevents the prior
     wrapping behavior where the two buttons stacked vertically and
     ate 90+px of action area height. */
  .v2-support-modal__buttons {
    width: 100%;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: stretch;
    gap: 10px;
  }
  .v2-support-modal__buttons .dw-back-pill {
    flex: 0 0 auto;
    min-width: 0;
  }
  .v2-support-modal__buttons .v2-support-modal__btn--primary {
    flex: 1 1 auto;
    min-width: 0;
    padding: 11px 18px;
    font-size: 15px;
    letter-spacing: 0.06em;
  }
  /* Agreement checkbox text trims to a single line on most devices;
     a slightly smaller font + tighter line-height keeps it from
     bleeding into the buttons even when wrapped to two lines. */
  .v2-support-modal__checkbox { font-size: 15px; line-height: 1.35; } /* 13 -> 15 — agreement option legibility (client 2026-05-29) */

  /* Event row stacks on narrow widths: city full-width, then state/date side-by-side. */
  .v2-support-modal__event-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      "city  city"
      "state date";
  }
  .v2-support-modal__field--grow { grid-area: city; }
  .v2-support-modal__field--state { grid-area: state; }
  .v2-support-modal__field--date { grid-area: date; }

  /* Slide-up on mobile */
  .v2-support-modal-enter-active .v2-support-modal__panel,
  .v2-support-modal-leave-active .v2-support-modal__panel {
    transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .v2-support-modal-enter-from .v2-support-modal__panel,
  .v2-support-modal-leave-to .v2-support-modal__panel {
    transform: translateY(100%);
    opacity: 1;
  }

  /* Mobile font bump — client feedback 2026-05-29.
     (a) iOS no-zoom guard: this modal lacked one, so its 14px form
         inputs/selects + 15px custom-amount input zoomed on focus in iOS
         Safari (and read small). Force them to 16px.
     (b) Legibility bumps on the genuinely-small readable text.
     Eyebrows + field labels (11px coordinated micro-labels) and the
     button labels are intentionally compact and left as-is. */
  .v2-support-modal__field input,
  .v2-support-modal__field select,
  .v2-support-modal__field textarea,
  .v2-support-modal__field--date input,
  .v2-support-modal__input-wrap input,
  .v2-support-modal__custom-pill input { font-size: 16px; }   /* 14/15 -> 16 (iOS no-zoom) */
  .v2-support-modal__section-prompt    { font-size: 16px; }   /* 14 -> 16 (form prompts) */
  .v2-support-modal__welcome p          { font-size: 16px; }   /* 14 -> 16 (welcome copy) */
  .v2-support-modal__intro              { font-size: 16px; }   /* 14 -> 16 (intro copy) */
  /* Rick 2026-06-06, bumped 2026-06-11 (21→24), nudged again 2026-06-11 (24→28
     per Rick: color's right, make the "Custom amount" placeholder slightly
     larger on mobile): lift the glyph, typed value, AND placeholder well above
     the preset-pill size so the custom amount is the prominent number (beats the
     base 18px !important on the input). Pairs with the darkened placeholder
     color above. */
  .v2-support-modal__custom-pill input  { font-size: 28px !important; }
  .v2-support-modal__custom-pill-glyph  { font-size: 28px; }
}

@media (max-width: 480px) {
  /* Native date controls and full state names need a complete phone row. */
  .v2-support-modal__event-row {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "city" "state" "date";
  }
  .v2-support-modal__event-row > .v2-support-modal__field,
  .v2-support-modal__field--date input { min-width: 0; }
  .v2-support-modal__field :deep(.inline-auth-container button[type="submit"]) {
    width: 100%;
    padding-inline: 14px;
  }
}

/* Preserve the photo on short phones and landscape while leaving enough
   height for the independently scrolling form and its pinned actions. */
@media (max-width: 1023px) and (max-height: 700px) {
  .v2sm-stepper { padding-bottom: 6px; }
  .v2-support-modal__heading { padding-top: 12px; padding-bottom: 8px; }
  .v2-support-modal__mobile-filler,
  .v2-support-modal__mobile-filler--step2 { height: 104px; }
  .v2-support-modal__step { gap: 14px; }
}

/* Laptop-height desktop windows (Brian 2026-09-27). The panel clamps to
   92vh, so below ~826px of viewport Step 2's ~743px (chrome + form) scrolled:
   36px at 1366x768, 80px at 1280x720. Reclaim the outer margin and tighten
   the vertical rhythm so the details form fits down to ~645px; the next tier
   drops the section eyebrows (hairlines and questions still separate the
   sections) to fit down to ~600px. */
@media (min-width: 1024px) and (max-height: 825px) {
  .v2-support-modal { padding: 16px; }
  .v2-support-modal__panel { height: min(760px, calc(100vh - 32px)); }
  .v2sm-stepper { padding-top: 16px; }
  .v2-support-modal__heading { font-size: 24px; padding-top: 6px; }
  .v2-support-modal__step-pane { padding-top: 4px; padding-bottom: 8px; }
  .v2-support-modal__actions { padding-top: 6px; padding-bottom: 14px; }
  .v2-support-modal__step--details { gap: 10px !important; }
  .v2-support-modal__section { gap: 8px; }
  .v2-support-modal__section + .v2-support-modal__section { padding-top: 8px; }
  .v2-support-modal__field--lead .v2-support-modal__field-hint { display: none; }
  .v2-support-modal__step--details .v2-support-modal__field input,
  .v2-support-modal__step--details .v2-support-modal__field select {
    min-height: 40px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .v2-support-modal__agreement-row { margin-top: 8px; padding-top: 8px; }
}
@media (min-width: 1024px) and (max-height: 660px) {
  .v2sm-stepper { padding-top: 10px; }
  .v2-support-modal__step--details .v2-support-modal__eyebrow { display: none; }
  /* Step 1's new-account form is the tallest auth state. */
  .v2-support-modal__field :deep(.auth-fields-wrapper),
  .v2-support-modal__field :deep(.inline-password-reset-container) { margin-top: 10px; padding: 14px; }
}
</style>
